// Unit Mining wizard — discover candidate units near a known property, stage
// and review them, enrich with owner/resident data, then push to HubSpot.
//
// Discovery, enrichment, staging, and the HubSpot push are all real now:
// Discovery and Enrichment call ATTOM via server/lib/unitMining/attomClient.js
// (see unitMiningService.js's discoverUnitsForType()/enrichUnit()), and each
// runs as a real per-item loop (one HTTP call per selected property type /
// per approved unit, awaited in sequence — same shape as the push loop below)
// rather than a simulated timer.

import React, { useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";

import { Circle, GoogleMap, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import { AdvancedMarker, buildUnitPinElement } from "./AdvancedMarker";

import "./UnitMiningWizard.css";

import { UserContext } from "../../../context/UserContext";
import { getAPICall, postAPICall } from "../../../config/apiCalls";
import { addToAutomationLog } from "../../../constants/logs";

import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Stepper from "@mui/material/Stepper";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";

import ApartmentIcon from "@mui/icons-material/Apartment";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import BusinessIcon from "@mui/icons-material/Business";
import CabinIcon from "@mui/icons-material/Cabin";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DataObjectIcon from "@mui/icons-material/DataObject";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HighlightAltIcon from "@mui/icons-material/HighlightAlt";
import HolidayVillageIcon from "@mui/icons-material/HolidayVillage";
import HouseIcon from "@mui/icons-material/House";
import LaunchIcon from "@mui/icons-material/Launch";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import PersonIcon from "@mui/icons-material/Person";
import PlaceIcon from "@mui/icons-material/Place";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RefreshIcon from "@mui/icons-material/Refresh";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import WarningIcon from "@mui/icons-material/Warning";

import { defaultBlue } from "../../../constants/theme";
import { ATTOM_TYPE_MAP, DEFAULT_RADIUS, PROPERTY_TYPES, RADIUS_OPTIONS } from "./unitMiningConstants";

const GOOGLE_MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
// Same loader id + libraries as PropertyViewer.js's own useJsApiLoader call —
// @react-google-maps/api shares one script load across every call site using
// the same id, so this reuses the already-loaded script instead of double-
// loading it.
const GOOGLE_MAPS_LIBRARIES = ["marker"];

const CIRCLE_OPTIONS = { strokeColor: defaultBlue, strokeWeight: 2, fillColor: defaultBlue, fillOpacity: 0.12 };

// Largest preset (RADIUS_OPTIONS) is 1000 ft — a custom radius beyond double
// that is flagged as likely to pull back a lot more candidates and use a lot
// more ATTOM credits per search, not blocked outright.
const CUSTOM_RADIUS_WARNING_THRESHOLD_FT = 2000;

const TYPE_ICON = {
	Apartment: LocationCityIcon,
	Condo: ApartmentIcon,
	"Duplex / Triplex / Quadplex": HolidayVillageIcon,
	"Single Family Residence": HouseIcon,
	"Mobile Home": CabinIcon,
};

const displayUnitStatus = (status) => (status === "Enriching" ? "In Enrichment" : status);

// First/last are kept as their own fields so either can contain spaces
// ("Mary Ann" / "Van Der Berg"). A record with neither set (saved before
// those fields existed, or just switched from corporate) falls back to the
// same first-word split the server uses (unitMiningService.js's ownerNameParts).
// Not trimmed here — a trailing space has to survive while it's being typed.
const ownerNameParts = (firstName, lastName, name) => {
	if (firstName != null || lastName != null) return { firstName: firstName || "", lastName: lastName || "" };
	const parts = (name || "").trim().split(/\s+/).filter(Boolean);
	return { firstName: parts[0] || "", lastName: parts.slice(1).join(" ") };
};
const primaryOwnerNameParts = (r) => ownerNameParts(r.firstName, r.lastName, r.name);
const secondOwnerNameParts = (r) => ownerNameParts(r.secondOwnerFirstName, r.secondOwnerLastName, r.secondOwnerName);
// A split always sets secondOwnerOutcome, so the co-owner section keys off
// that rather than the name — clearing both name fields mid-edit must not
// unmount the fields being typed in. Removing a co-owner is an explicit
// action (REMOVE_SECOND_OWNER) instead.
const hasSecondOwner = (r) => !!(r.secondOwnerName || r.secondOwnerOutcome);
const withoutSecondOwner = (r) => ({
	...r,
	secondOwnerName: "",
	secondOwnerFirstName: undefined,
	secondOwnerLastName: undefined,
	secondOwnerOutcome: "",
	secondOwnerRawTag: "",
	secondOwnerConflictOld: "",
	secondOwnerConflictContactId: "",
	secondOwnerConflictLastActivity: null,
	secondOwnerMatchedContactId: null,
	secondOwnerMatchedAt: null,
});

// Matches PropertyViewer.js's own formatDate() for consistency across the app.
const formatConflictDate = (value) => {
	if (!value) return null;
	const date = new Date(value);
	if (isNaN(date.getTime())) return null;
	return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// Standard HubSpot Contact object (0-1), same portal as PropertyViewer's own
// HubSpot links.
const HUBSPOT_CONTACT_RECORD_URL = (contactId) => `https://app.hubspot.com/contacts/9144184/record/0-1/${contactId}`;

const STEP_DEFS = [
	{ n: 1, title: "Launch" },
	{ n: 2, title: "Discovery" },
	{ n: 3, title: "Enrichment" },
	{ n: 4, title: "Push to HubSpot" },
];

const OUTCOME_CHIP_COLOR = {
	Matched: "success",
	"Corporate match": "primary",
	"Address conflict": "error",
	"No match": "default",
};

const OUTCOME_LABELS = ["All", "Matched", "Corporate match", "Address conflict", "Needs address review", "No match", "Approved"];

// "Needs address review" isn't a real outcome value (unlike the others in
// OUTCOME_LABELS) — it's the mailMatchesPropertyAddress flag, a source-data
// quality signal (an absentee owner's mailing address is just this unit's
// own address, so it's probably fake) that's independent of whether a
// HubSpot contact was matched/conflicted. Deliberately advisory, not a
// blocker like Address conflict: a row can still be marked Ready without
// ever running the People Data Labs lookup.
//
// Counted here as "still outstanding" only while pdlLookupAttempted is
// false — once a lookup's been run (found-and-accepted, found-and-discarded,
// or not found), a human has looked, so this stops nagging the same row even
// if the address itself is unchanged. Session-only, same as the flag it
// reads — this progress resets on reload, which is an accepted tradeoff, not
// a bug.
const needsAddressReviewOutstanding = (e) => !!e.mailMatchesPropertyAddress && !e.pdlLookupAttempted;

const matchesOutcomeFilter = (e, filter) => {
	if (filter === "All") return true;
	if (filter === "Approved") return e.approved;
	if (filter === "Needs address review") return needsAddressReviewOutstanding(e);
	return e.outcome === filter;
};

// Case-insensitive compare for click-to-sort table headers — null/undefined
// (e.g. no flag, no decision yet) sorts first.
const compareForSort = (a, b) => {
	const av = (a ?? "").toString().toLowerCase();
	const bv = (b ?? "").toString().toLowerCase();
	if (av < bv) return -1;
	if (av > bv) return 1;
	return 0;
};

const syncUnitStatuses = (units, enrich) =>
	units.map((u) => {
		const rec = enrich.find((e) => e.unitId === u.id);
		if (!rec) return u;
		const owner = rec.kind === "corporate" ? rec.company || rec.name : rec.name;
		if (rec.approved) return { ...u, status: "Ready", owner };
		if (rec.outcome === "Address conflict" || rec.outcome === "No match" || rec.secondOwnerOutcome === "Address conflict")
			return { ...u, status: "Needs Review", owner };
		return { ...u, status: "Enriching", owner };
	});

const maxStep = (state) => {
	if (state.pushRan || state.step === 4) return 4;
	if (state.enrich.length) return 3;
	return state.discovered ? 2 : 1;
};

const buildInitialState = () => ({
	step: 1,
	types: [...PROPERTY_TYPES],
	radius: DEFAULT_RADIUS,
	job: null, // { kind: "discovery" | "enrich" | "push", items, done, elapsedMs, calls }
	discovered: false,
	cands: [],
	candSearch: "",
	units: [],
	enrich: [],
	outcomeFilter: "All",
	conflictId: null,
	conflictPerson: null, // "primary" | "second" — which owner's dialog is open
	pdlLookupId: null, // enrich record id whose "look up real address" dialog is open
	pdlLookupStatus: null, // "loading" | "result" | "error" | null
	pdlLookupResult: null, // { status: "found" | "low_confidence" | "not_found", likelihood, mail1, mail2 }
	pdlLookupError: "",
	jsonId: null,
	confirmOpen: false,
	reconfirm: null, // "discovery" | "load" | null
	pushRan: false,
	retrying: false,
	pushResults: {},
	previewOpen: false,
	previewLoading: false,
	previewData: null, // { [unitId]: { unit: {...}, owner: {...}|null } | { error } }
	previewError: null,
	// False until the resume-in-progress-mining fetch on mount settles (success
	// or failure) — nothing about which step to land on is known before then,
	// so the wizard shows a loading placeholder instead of briefly flashing
	// Launch (the default step) before possibly jumping straight to Discovery/
	// Enrichment/Push once the real resume state comes back.
	hydrated: false,
});

// Applies a conflict resolution to whichever owner (primary or the split-
// trust co-owner) the dialog was opened for — both share the exact same
// resolution vocabulary/rawTags, just stored under "secondOwner*"-prefixed
// fields for the co-owner. `approved` only flips true once NEITHER owner
// has an unresolved conflict left, so resolving one doesn't silently
// approve a record whose other owner is still unresolved.
const CONFLICT_RESOLUTIONS = {
	same_update: { outcome: "Matched", rawTag: "merged_existing_contact", note: "Existing contact address updated" },
	same_keep: { outcome: "Matched", rawTag: "merged_keep_existing_address", note: "Existing HubSpot address kept as-is" },
	different: { outcome: "Matched", rawTag: "new_contact_created", note: "New contact will be created on push" },
};

// checkContactConflict (server-side, unitMiningService.js) only ever hands
// back the existing HubSpot address as one joined display string —
// `[address, city, state, zip].filter(Boolean).join(", ")` — there's no
// separate structured field for it. "Same person, keep old address" needs
// to refill the Mailing address / City-State-Zip inputs with that address
// (they otherwise still show the newly-parsed ATTOM address even though it
// won't be written to HubSpot for this resolution — see resolveOwner's
// rawTag check — which reads as the tool having ignored the choice), so
// this reverses that join. Heuristic: assumes the street address itself
// never contains a comma, true for every address this pipeline has
// produced so far, which lets a trailing zip and 2-letter state be peeled
// off unambiguously and leaves the rest to split between address and city.
// resolveOwner (server-side, unitMiningService.js) only ever writes an
// address update to an already-matched existing HubSpot contact when rawTag
// is "merged_existing_contact" — otherwise it reuses the cached match but
// leaves its address alone. That flag is normally only set by the address-
// conflict dialog's "Same person, update address" resolution, so without
// also setting it here, a manual correction to mail1/mail2 (or an accepted
// People Data Labs address) on a record that already has a real cached
// match would silently never reach HubSpot on push — "every field is
// editable" wouldn't actually be true for this case. Not needed when there's
// no real cached match (a fresh create always uses current properties
// regardless of rawTag) or when rawTag is already "new_contact_created" —
// promoting it there would wrongly turn off that resolution's force-create.
const shouldPromoteToMergedExisting = (matchedContactId, rawTag) =>
	!!matchedContactId && matchedContactId !== "none" && rawTag !== "new_contact_created";

const splitConflictOldAddress = (conflictOld) => {
	const parts = (conflictOld || "").split(",").map((p) => p.trim()).filter(Boolean);
	let zip = "";
	let state = "";
	if (parts.length && /^\d{5}(-\d{4})?$/.test(parts[parts.length - 1])) zip = parts.pop();
	if (parts.length && /^[A-Za-z]{2}$/.test(parts[parts.length - 1])) state = parts.pop();
	// With exactly one segment left, it's ambiguous whether that's a street
	// with no city or a city with no street (a HubSpot contact missing its
	// street address, city/state/zip only, is a real case) — a US street
	// address is essentially always led by a house number, so that's the tell
	// used to decide rather than always assuming "street".
	let city = "";
	if (parts.length > 1) city = parts.pop();
	else if (parts.length === 1 && !/^\d/.test(parts[0])) city = parts.pop();
	const mail1 = parts.join(", ");
	const mail2 = [city, state].filter(Boolean).join(", ") + (zip ? ` ${zip}` : "");
	return { mail1, mail2 };
};

const applyConflictResolution = (rec, person, resolution) => {
	const r = CONFLICT_RESOLUTIONS[resolution];
	let patched =
		person === "second"
			? { ...rec, secondOwnerOutcome: r.outcome, secondOwnerRawTag: r.rawTag }
			: { ...rec, outcome: r.outcome, rawTag: r.rawTag, note: r.note };
	// mail1/mail2 are shared with the co-owner (see the note on the conflict
	// dialog below), so only the primary owner's resolution ever touches
	// them — a second-owner "keep old" would otherwise clobber whatever the
	// primary already resolved, or fill in an address that belongs to a
	// different existing contact than the one the co-owner conflicted with.
	if (resolution === "same_keep" && person !== "second") {
		patched = { ...patched, ...splitConflictOldAddress(rec.conflictOld) };
	}
	const stillConflicted = patched.outcome === "Address conflict" || patched.secondOwnerOutcome === "Address conflict";
	return { ...patched, approved: !stillConflicted };
};

function reducer(state, action) {
	switch (action.type) {
		case "SET_TYPES":
			return { ...state, types: action.value };
		case "SET_RADIUS":
			return { ...state, radius: action.value };
		case "GO":
			return { ...state, step: action.value };
		case "JOB_START":
			return { ...state, job: { kind: action.kind, items: action.items, done: 0, elapsedMs: 0, calls: 0 } };
		case "JOB_TICK":
			return { ...state, job: { ...state.job, done: action.done, elapsedMs: action.elapsedMs, calls: action.calls } };
		case "JOB_CANCEL": {
			if (!state.job) return state;
			if (state.job.kind === "discovery") return { ...state, job: null, step: 1 };
			if (state.job.kind === "enrich") {
				return {
					...state,
					job: null,
					// Back to Discovery Review, not Enrichment Review — that's
					// where the resume action (the same Send to Enrichment
					// button) now lives.
					step: 2,
					units: state.units.map((u) => (u.status === "Enriching" ? { ...u, status: "Ready for Enrichment" } : u)),
				};
			}
			return state;
		}
		case "DISCOVERY_DONE":
			return { ...state, job: null, discovered: true, step: 2, cands: action.candidates };
		case "TOGGLE_CAND":
			return { ...state, cands: state.cands.map((c) => (c.id === action.id ? { ...c, sel: !c.sel } : c)) };
		case "DECIDE": {
			const { id, decision } = action;
			return {
				...state,
				cands: state.cands.map((c) => {
					if (c.id !== id) return c;
					const sel = decision === "reject" ? false : decision === "accept" ? true : c.sel;
					return { ...c, decision, sel };
				}),
			};
		}
		case "SET_CAND_SEARCH":
			return { ...state, candSearch: action.value };
		case "SET_CAND_SEL_MANY": {
			const ids = new Set(action.ids);
			return { ...state, cands: state.cands.map((c) => (ids.has(c.id) ? { ...c, sel: action.value } : c)) };
		}
		// action.units is the server's full current past-Discovery set
		// (everything promoted so far, not just this round's picks) —
		// anything whose id now shows up there has left Discovery Review;
		// whatever's left in cands genuinely wasn't picked and stays there
		// for a later pass.
		case "LOAD_SELECTED": {
			const stagedIds = new Set(action.units.map((u) => u.id));
			// No step change here — the enrichment job that immediately
			// follows (see doLoadSelected) owns the actual step transition
			// once it genuinely finishes; the job overlay covers the gap.
			return { ...state, cands: state.cands.filter((c) => !stagedIds.has(c.id)), units: action.units };
		}
		case "HYDRATE": {
			const pushRan = action.units.some((u) => u.status === "Pushed" || u.status === "Error");
			const pushResults = {};
			action.units.forEach((u) => {
				if (u.status === "Pushed") pushResults[u.id] = { res: "Pushed", hubspotUnitId: u.hubspotUnitId };
				else if (u.status === "Error") pushResults[u.id] = { res: "Error", error: u.pushError || "Unknown error" };
			});
			const next = {
				...state,
				cands: action.cands,
				units: action.units,
				enrich: action.enrich,
				discovered: action.cands.length > 0 || action.units.length > 0,
				pushRan,
				pushResults,
			};
			return { ...next, step: maxStep(next), hydrated: true };
		}
		case "HYDRATE_SETTLED":
			return { ...state, hydrated: true };
		case "SET_RECONFIRM":
			return { ...state, reconfirm: action.value };
		case "MARK_ENRICHING":
			return { ...state, units: state.units.map((u) => (action.ids.includes(u.id) ? { ...u, status: "Enriching" } : u)) };
		case "ENRICH_DONE": {
			const nextEnrich = [...state.enrich, ...action.records];
			return {
				...state,
				job: null,
				step: 3,
				enrich: nextEnrich,
				units: state.units.map((u) => {
					const rec = nextEnrich.find((e) => e.unitId === u.id);
					if (!rec) return u;
					const owner = rec.kind === "corporate" ? rec.company : rec.name;
					// Prefer the server-confirmed status (saveEnrichment's
					// return value) over recomputing it locally.
					const status =
						(action.statusesById && action.statusesById[u.id]) ||
						(rec.outcome === "Address conflict" || rec.outcome === "No match" || rec.secondOwnerOutcome === "Address conflict"
							? "Needs Review"
							: "Enriching");
					return { ...u, owner, status };
				}),
			};
		}
		case "EDIT_ENRICH": {
			// Editing the owner's name invalidates any cached HubSpot contact
			// match — it was found by searching for the OLD name, so it can't
			// be trusted for whatever name is here now. Clearing it just means
			// the next preview/push does one live search instead of reusing a
			// stale one; nothing is lost. Editing the shared mail1/mail2 fields
			// is different — see shouldPromoteToMergedExisting above.
			const isMailField = action.field === "mail1" || action.field === "mail2";
			return {
				...state,
				enrich: state.enrich.map((r) =>
					r.id === action.id
						? {
								...r,
								[action.field]: action.value,
								// Only corporate records edit "name" directly (as the entity
								// name) — drop any first/last so switching back to individual
								// re-derives them from the new name rather than showing stale ones.
								...(action.field === "name" ? { matchedContactId: null, matchedAt: null, firstName: undefined, lastName: undefined } : null),
								...(isMailField && shouldPromoteToMergedExisting(r.matchedContactId, r.rawTag) ? { rawTag: "merged_existing_contact" } : null),
								...(isMailField && shouldPromoteToMergedExisting(r.secondOwnerMatchedContactId, r.secondOwnerRawTag)
									? { secondOwnerRawTag: "merged_existing_contact" }
									: null),
							}
						: r,
				),
			};
		}
		case "EDIT_OWNER_NAME": {
			// action.part is "firstName" | "lastName"; action.second targets the
			// split co-owner. The combined name is kept in step for display and
			// for anything that still reads it. Same cached-match invalidation
			// as an EDIT_ENRICH name edit above.
			return {
				...state,
				enrich: state.enrich.map((r) => {
					if (r.id !== action.id) return r;
					if (action.second) {
						const parts = { ...secondOwnerNameParts(r), [action.part]: action.value };
						return {
							...r,
							secondOwnerFirstName: parts.firstName,
							secondOwnerLastName: parts.lastName,
							secondOwnerName: `${parts.firstName.trim()} ${parts.lastName.trim()}`.trim(),
							secondOwnerMatchedContactId: null,
							secondOwnerMatchedAt: null,
						};
					}
					const parts = { ...primaryOwnerNameParts(r), [action.part]: action.value };
					return {
						...r,
						firstName: parts.firstName,
						lastName: parts.lastName,
						name: `${parts.firstName.trim()} ${parts.lastName.trim()}`.trim(),
						matchedContactId: null,
						matchedAt: null,
					};
				}),
			};
		}
		case "REMOVE_SECOND_OWNER": {
			// Clears every co-owner field, including an unresolved Address
			// conflict that would otherwise keep the unit out of Ready — so unit
			// statuses are re-synced here, same as TOGGLE_APPROVED.
			const next = state.enrich.map((r) => (r.id === action.id ? withoutSecondOwner(r) : r));
			return { ...state, enrich: next, units: syncUnitStatuses(state.units, next) };
		}
		case "PICK_OUTCOME":
			return { ...state, outcomeFilter: action.value };
		case "SET_ENRICH_KIND":
			return { ...state, enrich: state.enrich.map((r) => (r.id === action.id ? { ...r, kind: action.value } : r)) };
		case "SET_ENRICH_ROLE":
			return { ...state, enrich: state.enrich.map((r) => (r.id === action.id ? { ...r, role: action.value } : r)) };
		case "TOGGLE_APPROVED": {
			const next = state.enrich.map((r) => (r.id === action.id ? { ...r, approved: !r.approved } : r));
			return { ...state, enrich: next, units: syncUnitStatuses(state.units, next) };
		}
		case "APPROVE_CONFIDENT": {
			const next = state.enrich.map((r) =>
				(r.outcome === "Matched" || r.outcome === "Corporate match") && r.secondOwnerOutcome !== "Address conflict" && !r.mailMatchesPropertyAddress
					? { ...r, approved: true }
					: r,
			);
			return { ...state, enrich: next, units: syncUnitStatuses(state.units, next) };
		}
		case "OPEN_CONFLICT":
			return { ...state, conflictId: action.id, conflictPerson: action.person || "primary" };
		case "RESOLVE_SAME_PERSON": {
			const next = state.enrich.map((r) => (r.id === state.conflictId ? applyConflictResolution(r, state.conflictPerson, "same_update") : r));
			return { ...state, enrich: next, units: syncUnitStatuses(state.units, next), conflictId: null, conflictPerson: null };
		}
		case "RESOLVE_NEW_PERSON": {
			const next = state.enrich.map((r) => (r.id === state.conflictId ? applyConflictResolution(r, state.conflictPerson, "different") : r));
			return { ...state, enrich: next, units: syncUnitStatuses(state.units, next), conflictId: null, conflictPerson: null };
		}
		case "RESOLVE_SAME_PERSON_KEEP_ADDRESS": {
			// Any rawTag other than "merged_existing_contact" leaves resolveOwner's
			// update-on-push branch untouched — the existing HubSpot contact gets
			// associated as-is, with no write to its address at all.
			const next = state.enrich.map((r) => (r.id === state.conflictId ? applyConflictResolution(r, state.conflictPerson, "same_keep") : r));
			return { ...state, enrich: next, units: syncUnitStatuses(state.units, next), conflictId: null, conflictPerson: null };
		}
		case "PDL_LOOKUP_START":
			return { ...state, pdlLookupId: action.id, pdlLookupStatus: "loading", pdlLookupResult: null, pdlLookupError: "" };
		case "PDL_LOOKUP_RESULT":
			// pdlLookupAttempted is separate from mailMatchesPropertyAddress —
			// discarding a real result (or a "not found") leaves the address
			// flag exactly as concerning as before, but the row shouldn't keep
			// showing the exact same "never looked at this" notice/button once
			// someone actually has. Session-only (not persisted) — matches every
			// other pure view-state flag in this reducer.
			return {
				...state,
				pdlLookupStatus: "result",
				pdlLookupResult: action.result,
				enrich: state.enrich.map((r) => (r.id === state.pdlLookupId ? { ...r, pdlLookupAttempted: true } : r)),
			};
		case "PDL_LOOKUP_ERROR":
			return { ...state, pdlLookupStatus: "error", pdlLookupError: action.message };
		case "PDL_LOOKUP_ACCEPT": {
			// Same rawTag promotion EDIT_ENRICH does for a manual mail1/mail2
			// edit — accepting a PDL-found address is exactly that, just sourced
			// from the lookup instead of typed in.
			const next = state.enrich.map((r) =>
				r.id === state.pdlLookupId
					? {
							...r,
							mail1: action.mail1,
							mail2: action.mail2,
							mailMatchesPropertyAddress: false,
							...(shouldPromoteToMergedExisting(r.matchedContactId, r.rawTag) ? { rawTag: "merged_existing_contact" } : null),
							...(shouldPromoteToMergedExisting(r.secondOwnerMatchedContactId, r.secondOwnerRawTag)
								? { secondOwnerRawTag: "merged_existing_contact" }
								: null),
						}
					: r,
			);
			return { ...state, enrich: next, pdlLookupId: null, pdlLookupStatus: null, pdlLookupResult: null, pdlLookupError: "" };
		}
		case "PDL_LOOKUP_CLOSE":
			return { ...state, pdlLookupId: null, pdlLookupStatus: null, pdlLookupResult: null, pdlLookupError: "" };
		case "GO_PUSH":
			return { ...state, step: 4 };
		case "OPEN_CONFIRM":
			return { ...state, confirmOpen: true };
		case "CONFIRM_PUSH_START":
			return { ...state, confirmOpen: false };
		case "OPEN_PREVIEW":
			return { ...state, previewOpen: true, previewLoading: true, previewData: null, previewError: null };
		case "PREVIEW_LOADED":
			return { ...state, previewLoading: false, previewData: action.preview };
		case "PREVIEW_ERROR":
			return { ...state, previewLoading: false, previewError: action.error };
		// Push runs as a real per-unit loop (see runPushLoop()) — each unit's
		// real result is recorded as soon as it's known, so the results table
		// reflects genuine progress rather than a simulated animation.
		case "RECORD_PUSH_RESULT":
			return {
				...state,
				pushResults: { ...state.pushResults, [action.id]: action.result },
				units: state.units.map((u) => (u.id === action.id ? { ...u, status: action.result.res === "Pushed" ? "Pushed" : "Error" } : u)),
			};
		case "PUSH_FINISHED":
			return { ...state, job: null, pushRan: true, retrying: false };
		case "RETRY_START":
			return { ...state, retrying: true };
		case "OPEN_JSON":
			return { ...state, jsonId: action.id };
		case "CLOSE_MODALS":
			return { ...state, jsonId: null, conflictId: null, conflictPerson: null, confirmOpen: false, reconfirm: null, previewOpen: false };
		default:
			return state;
	}
}

const UnitMiningWizard = ({ property, onFinish }) => {
	const [state, dispatch] = useReducer(reducer, undefined, buildInitialState);
	// Real per-item loops (discovery/enrichment/push) can't abort an in-flight
	// HTTP call, but they can stop before starting the next one and avoid
	// dispatching into a job the user already cancelled — checked the same
	// way DropboxInterrogator.js checks its own cancel-requested flag.
	const cancelledRef = useRef(false);
	// The job progress list auto-follows the active row as it advances — but
	// only until the user deliberately scrolls the list themselves, at which
	// point it stops nudging the scroll position for the rest of that run.
	const jobListRef = useRef(null);
	const activeJobRowRef = useRef(null);
	const jobAutoFollowRef = useRef(true);
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);

	// Map/satellite toggle, shared by the Launch and Discovery Review maps —
	// same "m"/"k" convention and default as PropertyViewer.js's own map.
	const [mapMode, setMapMode] = useState("k");
	// Separate flags (not one shared boolean) so each map's expanded dialog
	// is unambiguous even though only one step's map is ever visible at once.
	const [launchMapDialogOpen, setLaunchMapDialogOpen] = useState(false);
	const [discoveryMapDialogOpen, setDiscoveryMapDialogOpen] = useState(false);
	// Explicit, not inferred from whether state.radius happens to match a
	// preset string — the seeded custom value can legitimately collide with
	// a preset (e.g. the default "150 ft" is itself one of RADIUS_OPTIONS),
	// which made an inferred check get stuck showing the presets forever.
	const [customRadiusMode, setCustomRadiusMode] = useState(false);
	const mapTypeId = mapMode === "k" ? "satellite" : "roadmap";
	// Coordinate-based (not text-address) so it opens exactly where our own
	// map is centered, with no geocoding ambiguity.
	const gmapsUri = property.location ? `https://www.google.com/maps/search/?api=1&query=${property.location.lat},${property.location.lng}` : null;

	// Memoized so these object references only change when mapMode actually
	// does — GoogleMap re-applies `options` to the underlying map on every
	// render it sees a new object, so an inline literal here was forcing
	// that on every render of the whole wizard (any click anywhere), not
	// just an actual Map/Satellite toggle.
	const launchMapOptions = useMemo(
		() => ({
			streetViewControl: false,
			fullscreenControl: false,
			mapTypeControl: false,
			tiltControl: false,
			rotateControl: false,
			tilt: 0,
			// Required for AdvancedMarker (the property pin) to render at all —
			// without it Google Maps silently refuses advanced markers.
			mapId: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID,
			mapTypeId,
		}),
		[mapTypeId],
	);
	const discoveryMapOptions = useMemo(
		() => ({
			streetViewControl: false,
			fullscreenControl: false,
			mapTypeControl: false,
			tiltControl: false,
			rotateControl: false,
			tilt: 0,
			mapId: process.env.REACT_APP_GOOGLE_MAPS_MAP_ID,
			mapTypeId,
		}),
		[mapTypeId],
	);

	// Discovery Review's candidate map — ephemeral UI state, not worth
	// threading through the reducer.
	const [discoveryMapInstance, setDiscoveryMapInstance] = useState(null);
	const [activeCandMarkerKey, setActiveCandMarkerKey] = useState(null);
	// "Select on map" mode — clicking a pin toggles it into this running
	// selection instead of opening its InfoWindow, so several candidates
	// (e.g. an obviously-unrelated cluster) can be accepted/rejected at once.
	const [mapSelectMode, setMapSelectMode] = useState(false);
	const [mapSelectedIds, setMapSelectedIds] = useState(() => new Set());

	// Load Selected can move 80+ rows at once — real network latency, not
	// just perceived, so the button needs its own pending state.
	const [loadSelectedPending, setLoadSelectedPending] = useState(false);

	// Click-to-sort table headers — same convention as the rest of the site
	// (see ProjectViewer.js's photosSortKey/handlePhotosSort). Kept as plain
	// state rather than in the reducer since it's just view state, not data.
	const [candSortKey, setCandSortKey] = useState(null);
	const [candSortDir, setCandSortDir] = useState("asc");

	// Resume in-progress mining for this property instead of always starting
	// over at Launch — hydrates from whatever's already staged/enriched and
	// lands on the furthest reached step (maxStep() already knows how).
	useEffect(() => {
		if (!property?.id) {
			dispatch({ type: "HYDRATE_SETTLED" });
			return undefined;
		}
		let cancelled = false;
		getAPICall(instance, accounts[0], "/api/unitMining/units", { propertyId: property.id })
			.then((res) => {
				if (cancelled) return;
				const rows = res.data.units || [];
				// "Discovered" rows are candidates still under review — never
				// promoted yet — everything else is a unit somewhere past that.
				const candRows = rows.filter((r) => r.status === "Discovered");
				const stagedRows = rows.filter((r) => r.status !== "Discovered");
				const cands = candRows.map((c) => ({
					id: c.id,
					a1: c.a1,
					a2: c.a2,
					csz: c.csz,
					type: c.type,
					flag: c.flag,
					providerId: c.providerId,
					providerRawJson: c.providerRawJson || null,
					sel: c.sel,
					decision: c.decision,
					lat: c.lat,
					lng: c.lng,
				}));
				const units = stagedRows.map(({ enrichment, ...u }) => ({ ...u, sel: true }));
				const enrich = stagedRows
					.filter((u) => u.enrichment)
					.map((u) => ({
						id: `e_${u.id}`,
						unitId: u.id,
						unit: u.a2 || "—",
						providerId: u.enrichment.providerId,
						name: u.enrichment.name,
						firstName: u.enrichment.firstName,
						lastName: u.enrichment.lastName,
						company: u.enrichment.company,
						kind: u.enrichment.kind,
						role: u.enrichment.role,
						occupancy: u.enrichment.occupancy,
						mail1: u.enrichment.mail1,
						mail2: u.enrichment.mail2,
						mailMatchesPropertyAddress: u.enrichment.mailMatchesPropertyAddress || false,
						outcome: u.enrichment.outcome,
						rawTag: u.enrichment.rawTag,
						note: u.enrichment.note,
						conflictOld: u.enrichment.conflictOld || "",
						conflictContactId: u.enrichment.conflictContactId || "",
						conflictLastActivity: u.enrichment.conflictLastActivity || null,
						matchedContactId: u.enrichment.matchedContactId || null,
						matchedAt: u.enrichment.matchedAt || null,
						secondOwnerName: u.enrichment.secondOwnerName || "",
						secondOwnerFirstName: u.enrichment.secondOwnerFirstName,
						secondOwnerLastName: u.enrichment.secondOwnerLastName,
						secondOwnerOutcome: u.enrichment.secondOwnerOutcome || "",
						secondOwnerRawTag: u.enrichment.secondOwnerRawTag || "",
						secondOwnerConflictOld: u.enrichment.secondOwnerConflictOld || "",
						secondOwnerConflictContactId: u.enrichment.secondOwnerConflictContactId || "",
						secondOwnerConflictLastActivity: u.enrichment.secondOwnerConflictLastActivity || null,
						secondOwnerMatchedContactId: u.enrichment.secondOwnerMatchedContactId || null,
						secondOwnerMatchedAt: u.enrichment.secondOwnerMatchedAt || null,
						providerRawJson: u.enrichment.providerRawJson || null,
						approved: u.enrichment.approved,
					}));
				dispatch({ type: "HYDRATE", cands, units, enrich });
			})
			.catch((err) => {
				console.error("[UnitMiningWizard] failed to load existing units:", err.message);
				if (!cancelled) dispatch({ type: "HYDRATE_SETTLED" });
			});
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [property?.id]);

	// Keeps the active row of the discovery/enrichment job list scrolled into
	// view as it advances, as long as the user hasn't taken over scrolling
	// themselves (see the onWheel/onTouchMove handlers on the list itself).
	useEffect(() => {
		if (!state.job || state.job.kind === "push") return;
		if (!jobAutoFollowRef.current) return;
		activeJobRowRef.current?.scrollIntoView({ block: "nearest" });
	}, [state.job && state.job.done]);

	const { isLoaded: isMapsLoaded, loadError: mapsLoadError } = useJsApiLoader({
		id: "j2-property-viewer-google-map",
		googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
		libraries: GOOGLE_MAPS_LIBRARIES,
	});

	// Candidates sharing a (near-enough) rooftop coordinate — e.g. every unit
	// in one building — get combined into a single map pin instead of
	// stacking identical markers on top of each other, same pattern
	// PropertyViewer.js uses for its own Units map.
	const candMarkerGroups = [];
	{
		const groupIndexByKey = new Map();
		state.cands.forEach((c) => {
			if (c.lat == null || c.lng == null) return;
			const key = `${c.lat.toFixed(5)},${c.lng.toFixed(5)}`;
			if (groupIndexByKey.has(key)) {
				candMarkerGroups[groupIndexByKey.get(key)].cands.push(c);
			} else {
				groupIndexByKey.set(key, candMarkerGroups.length);
				candMarkerGroups.push({ key, location: { lat: c.lat, lng: c.lng }, cands: [c] });
			}
		});
	}
	// Green/red only when every candidate at that pin agrees on a decision —
	// grey for undecided or mixed, same vocabulary as the table's row tints.
	const candGroupColor = (group) => {
		if (group.cands.every((c) => c.decision === "accept")) return "#2e7d32";
		if (group.cands.every((c) => c.decision === "reject")) return "#b53943";
		if (group.cands.every((c) => c.decision === "uncertain")) return "#ed6c02";
		return "#5b6670";
	};

	// Reference-only pins for units already past Discovery (Enrichment
	// Review / Push) — spatial context on the map, but intentionally never
	// added to the candidate table below; nothing here is interactive
	// beyond a read-only info popup.
	const referenceMarkerGroups = [];
	{
		const groupIndexByKey = new Map();
		state.units.forEach((u) => {
			if (u.lat == null || u.lng == null) return;
			const key = `${u.lat.toFixed(5)},${u.lng.toFixed(5)}`;
			if (groupIndexByKey.has(key)) {
				referenceMarkerGroups[groupIndexByKey.get(key)].units.push(u);
			} else {
				groupIndexByKey.set(key, referenceMarkerGroups.length);
				referenceMarkerGroups.push({ key: `ref_${key}`, location: { lat: u.lat, lng: u.lng }, units: [u] });
			}
		});
	}
	// Muted blue while still in the enrichment pipeline, muted blue-grey
	// once pushed (or push-failed) — deliberately not the same palette as
	// the candidate pins, so these read as reference, not something to act on.
	const referenceGroupColor = (group) => (group.units.every((u) => u.status === "Pushed" || u.status === "Error") ? "#78909c" : "#184c93");

	// Re-fits whenever the candidate/reference sets change (a fresh
	// discovery run, a promote, or hydrating from the DB) — not on every
	// render, so an accept/reject click doesn't yank the viewport around.
	useEffect(() => {
		if (!discoveryMapInstance || !window.google?.maps) return;
		const bounds = new window.google.maps.LatLngBounds();
		let hasAny = false;
		if (property.location) {
			bounds.extend(property.location);
			hasAny = true;
		}
		candMarkerGroups.forEach((g) => {
			bounds.extend(g.location);
			hasAny = true;
		});
		referenceMarkerGroups.forEach((g) => {
			bounds.extend(g.location);
			hasAny = true;
		});
		if (hasAny) discoveryMapInstance.fitBounds(bounds, 40);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [discoveryMapInstance, candMarkerGroups.length, referenceMarkerGroups.length]);

	// Fire-and-forget persistence for enrichment edits — dispatched locally
	// first for a snappy UI, saved here in the background so a lost network
	// call doesn't block typing or clicking.
	const persistEnrichmentRecord = (record) => {
		postAPICall(instance, accounts[0], "/api/unitMining/saveEnrichment", { unitId: record.unitId, record }, employeeID).catch((err) =>
			console.error("[UnitMiningWizard] failed to save enrichment:", err.message),
		);
	};

	// Fire-and-forget persistence for candidate-review edits (checkbox,
	// accept/reject, inline address edits) — same pattern as
	// persistEnrichmentRecord above, so a reload never loses review progress.
	const persistCandidateFields = (id, fields) => {
		postAPICall(instance, accounts[0], "/api/unitMining/updateCandidate", { id, fields }, employeeID).catch((err) =>
			console.error("[UnitMiningWizard] failed to update candidate:", err.message),
		);
	};

	const cancelJob = () => {
		cancelledRef.current = true;
		dispatch({ type: "JOB_CANCEL" });
	};

	// All selected UI types resolve to their ATTOM keyword(s) (ATTOM_TYPE_MAP)
	// and go into ONE combined /property/address call, joined with "|" —
	// confirmed live that ATTOM's propertyType param accepts pipe-delimited
	// OR-combination and returns the exact deduped union of what separate
	// per-keyword calls would (verified against real result ids), so a single
	// request replaces what used to be one sequential call per keyword.
	const doRunDiscovery = async () => {
		cancelledRef.current = false;
		jobAutoFollowRef.current = true;
		const attomTypes = state.types.flatMap((t) => ATTOM_TYPE_MAP[t] || []);
		const typeLabel = state.types.join(", ");
		const items = [{ label: `${typeLabel} · ${state.radius} radius`, sub: "Searching ATTOM (combined query)", doneText: "results returned" }];
		dispatch({ type: "JOB_START", kind: "discovery", items });
		const t0 = Date.now();
		const radiusFeet = parseInt(state.radius, 10);
		const propertyLabel = property.name || property.addressLine1 || "an unnamed property";
		let all = [];
		try {
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/unitMining/discover",
				{ propertyId: property.id, propertyType: attomTypes.join("|"), radiusFeet, typeLabel },
				employeeID,
			);
			all = res.data.candidates || [];
			addToAutomationLog(
				instance,
				accounts[0],
				employeeID,
				true,
				`ATTOM discovery: ${all.length} candidate(s) found for ${typeLabel} within ${state.radius} of ${propertyLabel}.`,
				"Unit Mining - ATTOM",
			);
		} catch (err) {
			console.error("[UnitMiningWizard] discovery call failed:", err.message);
			addToAutomationLog(
				instance,
				accounts[0],
				employeeID,
				false,
				`ATTOM discovery failed for ${typeLabel} within ${state.radius} of ${propertyLabel}. ${err?.response?.data?.error || err.message}`,
				"Unit Mining - ATTOM",
			);
		}
		if (cancelledRef.current) return;
		dispatch({ type: "JOB_TICK", done: 1, elapsedMs: Date.now() - t0, calls: 4 });

		// A unit can theoretically surface more than once within a combined
		// result set (e.g. straddling two of the searched keywords) — dedupe
		// across the whole batch just returned.
		const seen = new Set();
		const deduped = [];
		for (const c of all) {
			const key = `${c.a1} ${c.a2} ${c.csz}`.toLowerCase();
			if (seen.has(key)) continue;
			seen.add(key);
			deduped.push({ ...c, sel: !c.flag, decision: !c.flag ? "accept" : null });
		}

		// Persisted immediately (status "Discovered") so a reload resumes this
		// review screen instead of losing it — real DB ids come back here and
		// are what every review edit below is keyed on.
		try {
			const saveRes = await postAPICall(
				instance,
				accounts[0],
				"/api/unitMining/saveDiscoveredCandidates",
				{ propertyId: property.id, candidates: deduped },
				employeeID,
			);
			dispatch({ type: "DISCOVERY_DONE", candidates: saveRes.data.candidates || [] });
		} catch (err) {
			console.error("[UnitMiningWizard] failed to save discovered candidates:", err.message);
			dispatch({ type: "DISCOVERY_DONE", candidates: deduped });
		}
	};

	const toggleCand = (c) => {
		dispatch({ type: "TOGGLE_CAND", id: c.id });
		persistCandidateFields(c.id, { sel: !c.sel });
	};

	const decideCand = (c, decision) => {
		const sel = decision === "reject" ? false : decision === "accept" ? true : c.sel;
		dispatch({ type: "DECIDE", id: c.id, decision });
		persistCandidateFields(c.id, { decision, sel });
	};

	// A pin can represent a cluster of several candidates sharing one
	// coordinate — clicking it while in map-select mode toggles all of them
	// together, matching the granularity of what's actually visible on screen.
	const toggleMapGroupSelection = (group) => {
		setMapSelectedIds((prev) => {
			const next = new Set(prev);
			const allSelected = group.cands.every((c) => next.has(c.id));
			group.cands.forEach((c) => (allSelected ? next.delete(c.id) : next.add(c.id)));
			return next;
		});
	};

	const bulkDecideMapSelection = (decision) => {
		state.cands.filter((c) => mapSelectedIds.has(c.id)).forEach((c) => decideCand(c, decision));
		setMapSelectedIds(new Set());
	};

	const toggleMapSelectMode = () => {
		setMapSelectMode((v) => !v);
		setMapSelectedIds(new Set());
	};

	// Same 3-click cycle as TanStack Table's default sorting toggle: none ->
	// asc -> desc -> none.
	const handleCandSort = (key) => {
		if (key !== candSortKey) {
			setCandSortKey(key);
			setCandSortDir("asc");
		} else if (candSortDir === "asc") {
			setCandSortDir("desc");
		} else {
			setCandSortKey(null);
			setCandSortDir("asc");
		}
	};

	// Same icon convention as TanStackTable.js (used by the Admin consoles) —
	// a neutral unfold icon on every sortable header, swapped for a filled
	// up/down chevron once that column is the active sort.
	const renderSortIcon = (isActive, dir) =>
		isActive ? dir === "asc" ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" /> : <UnfoldMoreIcon fontSize="small" />;

	const setCandSelMany = (ids, value) => {
		dispatch({ type: "SET_CAND_SEL_MANY", ids, value });
		ids.forEach((id) => persistCandidateFields(id, { sel: value }));
	};

	// Toggles just the accepted candidates' own checkboxes — leaves anything
	// else already selected (e.g. via map multiselect) untouched.
	const toggleSelectAccepted = () => {
		const accepted = state.cands.filter((c) => c.decision === "accept");
		const allSelected = accepted.length > 0 && accepted.every((c) => c.sel);
		const nextSel = !allSelected;
		dispatch({ type: "SET_CAND_SEL_MANY", ids: accepted.map((c) => c.id), value: nextSel });
		accepted.forEach((c) => {
			if (c.sel !== nextSel) persistCandidateFields(c.id, { sel: nextSel });
		});
	};

	const runDiscovery = () => {
		if (state.discovered) {
			dispatch({ type: "SET_RECONFIRM", value: "discovery" });
			return;
		}
		doRunDiscovery();
	};

	// Promotes whatever's picked in Discovery Review, then immediately sends
	// the resulting Staging-free set straight into enrichment — the two used
	// to be separate user actions (Load Selected, then a Staging "Send to
	// Enrichment" button); now it's one. Passing `units` explicitly into
	// sendToEnrichment rather than letting it read `state.units` matters
	// here: state won't have re-rendered with the dispatch above yet by the
	// time this function keeps running.
	const doLoadSelected = async () => {
		const picked = state.cands.filter((c) => c.sel && c.decision !== "reject");
		setLoadSelectedPending(true);
		try {
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/unitMining/promoteCandidates",
				{ propertyId: property.id, ids: picked.map((c) => c.id) },
				employeeID,
			);
			const units = (res.data.units || []).map((u) => ({ ...u, sel: true }));
			dispatch({ type: "LOAD_SELECTED", units });
			await sendToEnrichment(units);
		} catch (err) {
			console.error("[UnitMiningWizard] failed to load units:", err.message);
		} finally {
			setLoadSelectedPending(false);
		}
	};

	// Load Selected no longer needs a reconfirm gate — promoting is
	// non-destructive now (un-picked candidates just stay in Discovery
	// Review), so there's nothing left to warn about.
	const proceedReconfirm = () => {
		const kind = state.reconfirm;
		dispatch({ type: "SET_RECONFIRM", value: null });
		if (kind === "discovery") doRunDiscovery();
	};

	// One ATTOM /property/basicprofile call per approved unit — enrichUnit()
	// server-side does the ATTOM call, parsing, and persistence in one shot
	// and returns the final record + status, so the loop here just drives
	// real progress, same shape as runPushLoop() below.
	const sendToEnrichment = async (unitsOverride) => {
		const picked = (unitsOverride || state.units).filter((u) => u.status === "Ready for Enrichment");
		if (!picked.length) return;
		try {
			await postAPICall(instance, accounts[0], "/api/unitMining/markEnriching", { ids: picked.map((u) => u.id) }, employeeID);
		} catch (err) {
			console.error("[UnitMiningWizard] failed to mark units enriching:", err.message);
		}
		dispatch({ type: "MARK_ENRICHING", ids: picked.map((u) => u.id) });

		cancelledRef.current = false;
		jobAutoFollowRef.current = true;
		const items = picked.map((u) => ({ label: `${u.a2 || u.a1} · ${u.a1}`, sub: "Owner and resident lookup", doneText: "record returned" }));
		dispatch({ type: "JOB_START", kind: "enrich", items });
		const t0 = Date.now();
		const propertyLabel = property.name || property.addressLine1 || "an unnamed property";
		const records = [];
		const statusesById = {};
		for (let i = 0; i < picked.length; i++) {
			const u = picked[i];
			const unitLabel = u.a2 || u.a1;
			try {
				const res = await postAPICall(instance, accounts[0], "/api/unitMining/enrichUnit", { unitId: u.id }, employeeID);
				if (res.data.record) {
					records.push({ id: `e_${u.id}`, ...res.data.record });
					statusesById[u.id] = res.data.status;
				}
				addToAutomationLog(
					instance,
					accounts[0],
					employeeID,
					true,
					`ATTOM enrichment: looked up owner for ${unitLabel} at ${propertyLabel}.`,
					"Unit Mining - ATTOM",
				);
			} catch (err) {
				console.error("[UnitMiningWizard] enrichment call failed:", err.message);
				addToAutomationLog(
					instance,
					accounts[0],
					employeeID,
					false,
					`ATTOM enrichment failed for ${unitLabel} at ${propertyLabel}. ${err?.response?.data?.error || err.message}`,
					"Unit Mining - ATTOM",
				);
			}
			if (cancelledRef.current) return;
			dispatch({ type: "JOB_TICK", done: i + 1, elapsedMs: Date.now() - t0, calls: i + 1 });
		}
		dispatch({ type: "ENRICH_DONE", records, statusesById });
	};

	const removeSecondOwner = (id) => {
		const rec = state.enrich.find((r) => r.id === id);
		if (!rec) return;
		dispatch({ type: "REMOVE_SECOND_OWNER", id });
		persistEnrichmentRecord(withoutSecondOwner(rec));
	};

	const approveEnrich = (id) => {
		const rec = state.enrich.find((r) => r.id === id);
		if (!rec) return;
		if (rec.outcome === "Address conflict" && !rec.approved) {
			dispatch({ type: "OPEN_CONFLICT", id, person: "primary" });
			return;
		}
		if (rec.secondOwnerOutcome === "Address conflict" && !rec.approved) {
			dispatch({ type: "OPEN_CONFLICT", id, person: "second" });
			return;
		}
		dispatch({ type: "TOGGLE_APPROVED", id });
		persistEnrichmentRecord({ ...rec, approved: !rec.approved });
	};

	// "Needs address review" is deliberately excluded from this bulk sweep —
	// still advisory (a row can still be marked Ready via the per-row button
	// below), but a one-click "approve everything confident" pass shouldn't
	// be the thing that waves through a mailing address that's probably fake
	// without a human ever having looked at it.
	const approveConfident = () => {
		const affected = state.enrich.filter(
			(r) =>
				(r.outcome === "Matched" || r.outcome === "Corporate match") &&
				r.secondOwnerOutcome !== "Address conflict" &&
				!r.mailMatchesPropertyAddress &&
				!r.approved,
		);
		dispatch({ type: "APPROVE_CONFIDENT" });
		affected.forEach((r) => persistEnrichmentRecord({ ...r, approved: true }));
	};

	const setEnrichKind = (id, value) => {
		const rec = state.enrich.find((r) => r.id === id);
		dispatch({ type: "SET_ENRICH_KIND", id, value });
		if (rec) persistEnrichmentRecord({ ...rec, kind: value });
	};

	const setEnrichRole = (id, value) => {
		const rec = state.enrich.find((r) => r.id === id);
		dispatch({ type: "SET_ENRICH_ROLE", id, value });
		if (rec) persistEnrichmentRecord({ ...rec, role: value });
	};

	const resolveSamePerson = () => {
		const rec = state.enrich.find((r) => r.id === state.conflictId);
		if (!rec) return;
		dispatch({ type: "RESOLVE_SAME_PERSON" });
		persistEnrichmentRecord(applyConflictResolution(rec, state.conflictPerson, "same_update"));
	};

	const resolveNewPerson = () => {
		const rec = state.enrich.find((r) => r.id === state.conflictId);
		if (!rec) return;
		dispatch({ type: "RESOLVE_NEW_PERSON" });
		persistEnrichmentRecord(applyConflictResolution(rec, state.conflictPerson, "different"));
	};

	const resolveSamePersonKeepAddress = () => {
		const rec = state.enrich.find((r) => r.id === state.conflictId);
		if (!rec) return;
		dispatch({ type: "RESOLVE_SAME_PERSON_KEEP_ADDRESS" });
		persistEnrichmentRecord(applyConflictResolution(rec, state.conflictPerson, "same_keep"));
	};

	// People Data Labs is billed per lookup, so this only ever fires on a
	// deliberate click (never as part of the normal enrichment loop above) —
	// the button itself only shows on a row the server has already flagged as
	// mailMatchesPropertyAddress, and lookupOwnerAddressViaPDL re-checks that
	// server-side too. Never writes mail1/mail2 itself; the result just opens
	// this same accept/discard review the address-conflict dialog uses, so a
	// low-confidence (or simply wrong-person) match never gets trusted blind.
	const runPdlLookup = (id) => {
		const rec = state.enrich.find((r) => r.id === id);
		if (!rec) return;
		dispatch({ type: "PDL_LOOKUP_START", id });
		postAPICall(instance, accounts[0], "/api/unitMining/lookupOwnerAddress", { unitId: rec.unitId }, employeeID)
			.then((res) => dispatch({ type: "PDL_LOOKUP_RESULT", result: res.data }))
			.catch((err) => dispatch({ type: "PDL_LOOKUP_ERROR", message: err?.response?.data?.error || err.message }));
	};

	const acceptPdlLookup = () => {
		const rec = state.enrich.find((r) => r.id === state.pdlLookupId);
		const result = state.pdlLookupResult;
		if (!rec || !result) return;
		dispatch({ type: "PDL_LOOKUP_ACCEPT", mail1: result.mail1, mail2: result.mail2 });
		persistEnrichmentRecord({
			...rec,
			mail1: result.mail1,
			mail2: result.mail2,
			mailMatchesPropertyAddress: false,
			...(shouldPromoteToMergedExisting(rec.matchedContactId, rec.rawTag) ? { rawTag: "merged_existing_contact" } : null),
			...(shouldPromoteToMergedExisting(rec.secondOwnerMatchedContactId, rec.secondOwnerRawTag) ? { secondOwnerRawTag: "merged_existing_contact" } : null),
		});
	};

	// Push (and retry) run as a real client-driven loop, same shape as
	// doRunDiscovery()/sendToEnrichment() above — one HTTP call per unit,
	// awaited in order, matching the app's established pattern for
	// long-running work (see DropboxInterrogator.js). No cancel affordance is
	// offered for push specifically (the UI already says this write can't be
	// cancelled), so this one doesn't check cancelledRef.
	const runPushLoop = async (units, endpoint, extraBody = {}, runLabel = "Push") => {
		const items = units.map((u) => ({ label: `${u.a2 || u.a1} · ${u.owner || "no contact"}`, sub: "Creating unit and contact association" }));
		dispatch({ type: "JOB_START", kind: "push", items });
		const t0 = Date.now();
		const propertyLabel = property.name || property.addressLine1 || "an unnamed property";
		for (let i = 0; i < units.length; i++) {
			const u = units[i];
			const unitLabel = u.a2 || u.a1;
			let result;
			try {
				const res = await postAPICall(instance, accounts[0], endpoint, { ...extraBody, unitIds: [u.id] }, employeeID);
				result = (res.data.results && res.data.results[u.id]) || { res: "Error", error: "No result returned" };
			} catch (err) {
				result = { res: "Error", error: err?.response?.data?.error || err.message };
			}
			// One log entry per unit as it's individually pushed, not a single
			// batch summary — each unit is its own HubSpot write, so it gets
			// its own automation-log record.
			const success = result.res !== "Error";
			const message = success
				? `${runLabel}: pushed ${unitLabel} to HubSpot for ${propertyLabel}.`
				: `${runLabel}: failed to push ${unitLabel} to HubSpot for ${propertyLabel}. ${result.error || ""}`.trim();
			addToAutomationLog(instance, accounts[0], employeeID, success, message, "Unit Mining");
			dispatch({ type: "RECORD_PUSH_RESULT", id: u.id, result });
			dispatch({ type: "JOB_TICK", done: i + 1, elapsedMs: Date.now() - t0, calls: (i + 1) * 2 });
		}
		dispatch({ type: "PUSH_FINISHED" });
	};

	const confirmPush = () => {
		const ready = state.units.filter((u) => u.status === "Ready");
		dispatch({ type: "CONFIRM_PUSH_START" });
		runPushLoop(ready, "/api/unitMining/push", { propertyId: property.id }, "Push");
	};

	// Read-only — shows exactly what pushing would send without writing
	// anything to HubSpot, so nothing about the push is a surprise.
	const openPreview = async () => {
		const ready = state.units.filter((u) => u.status === "Ready");
		dispatch({ type: "OPEN_PREVIEW" });
		try {
			const res = await postAPICall(instance, accounts[0], "/api/unitMining/previewPush", { unitIds: ready.map((u) => u.id) }, employeeID);
			dispatch({ type: "PREVIEW_LOADED", preview: res.data.preview });
		} catch (err) {
			dispatch({ type: "PREVIEW_ERROR", error: err?.response?.data?.error || err.message });
		}
	};

	const retryFailed = () => {
		const failedIds = Object.keys(state.pushResults).filter((id) => state.pushResults[id].res === "Error");
		const rows = state.units.filter((u) => failedIds.includes(String(u.id)));
		dispatch({ type: "RETRY_START" });
		runPushLoop(rows, "/api/unitMining/retryFailed", {}, "Retry");
	};

	const finishRun = () => onFinish();

	// Enrichment already ran isn't a one-way gate on its own anymore — only
	// an actual HubSpot write is — so re-discovering/loading more later
	// stays possible even after earlier candidates have been enriched.
	const goStep = (n) => {
		if (state.pushRan) return;
		if (n > maxStep(state)) return;
		dispatch({ type: "GO", value: n });
	};

	const showJobOverlay = !!state.job && state.job.kind !== "push";

	// ---- stepper --------------------------------------------------------
	const renderStepper = () => {
		const locked = state.pushRan;
		const max = maxStep(state);
		return (
			<Stepper activeStep={state.step - 1} nonLinear sx={{ px: 3, py: 2.5, bgcolor: "grey.50", borderBottom: "1px solid rgba(0,0,0,.12)" }}>
				{STEP_DEFS.map((d) => {
					const done = d.n < state.step;
					const reachable = locked ? d.n === state.step : d.n <= max;
					return (
						<Step key={d.n} completed={done}>
							<StepButton disabled={!reachable} onClick={() => goStep(d.n)}>
								{d.title}
							</StepButton>
						</Step>
					);
				})}
			</Stepper>
		);
	};

	// ---- job progress (discovery / enrichment) ---------------------------
	const renderJobPanel = () => {
		const job = state.job;
		const title = job.kind === "discovery" ? "Running discovery" : "Running enrichment";
		const sub =
			job.kind === "discovery"
				? `Nationwide radius search from ${property.addressLine1 || "the property"} · ${state.radius}`
				: `${job.items.length} staged units sent for owner and resident lookup`;
		const note =
			job.kind === "discovery"
				? "Results are held for review. Nothing is written to HubSpot by this run."
				: "Cancelling returns these units to Ready for Enrichment without enriching them.";
		const pct = job.items.length ? (job.done / job.items.length) * 100 : 0;
		return (
			<div>
				<Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={2} flexWrap="wrap">
					<div>
						<div style={{ fontSize: 16, fontWeight: 500 }}>{title}</div>
						<div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,.6)" }}>{sub}</div>
					</div>
					<div style={{ fontSize: 12, color: "rgba(0,0,0,.6)", fontVariantNumeric: "tabular-nums" }}>
						{job.done} of {job.items.length} · {job.calls} provider calls · {(job.elapsedMs / 1000).toFixed(1)}s
					</div>
				</Stack>
				<LinearProgress variant="determinate" value={pct} sx={{ mt: 2 }} />
				<div
					className="unitMiningJobList"
					ref={jobListRef}
					onWheel={() => { jobAutoFollowRef.current = false; }}
					onTouchMove={() => { jobAutoFollowRef.current = false; }}
				>
					{job.items.map((it, i) => {
						const rowStatus = i < job.done ? "done" : i === job.done ? "active" : "pending";
						return (
							<div key={i} ref={rowStatus === "active" ? activeJobRowRef : null} className={`unitMiningJobRow ${rowStatus}`}>
								{rowStatus === "done" ? (
									<CheckCircleIcon fontSize="small" color="success" />
								) : rowStatus === "active" ? (
									<AutorenewIcon fontSize="small" color="primary" className="unitMiningJobIcon spin" />
								) : (
									<RadioButtonUncheckedIcon fontSize="small" color="disabled" />
								)}
								<div className="unitMiningJobRowLabel">{it.label}</div>
								<div className="unitMiningJobRowSub">{rowStatus === "done" ? it.doneText || "done" : rowStatus === "active" ? it.sub : "Queued"}</div>
							</div>
						);
					})}
				</div>
				<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flexWrap="wrap" sx={{ mt: 2, pt: 1.75, borderTop: "1px solid rgba(0,0,0,.08)" }}>
					<div style={{ fontSize: 12, color: "rgba(0,0,0,.5)" }}>{note}</div>
					<Button variant="outlined" size="small" onClick={cancelJob}>
						Cancel Run
					</Button>
				</Stack>
			</div>
		);
	};

	// ---- step 1: launch ---------------------------------------------------
	const renderLaunchMap = (radiusMeters) => {
		if (mapsLoadError) return <div className="propertyViewerEmptyStateMessage">Failed to load Google Maps.</div>;
		if (!isMapsLoaded) return <div className="propertyViewerEmptyStateMessage">Loading map…</div>;
		if (!property.location) return <div className="propertyViewerEmptyStateMessage">No coordinates on file for this property.</div>;
		return (
			<GoogleMap
				mapContainerStyle={GOOGLE_MAP_CONTAINER_STYLE}
				center={property.location}
				zoom={17}
				mapTypeId={mapTypeId}
				// @react-google-maps/api constructs the underlying google.maps.Map
				// using only the `options` prop, then applies `mapTypeId` a tick
				// later as a separate update — baking it into `options` too means
				// the map is CREATED as satellite/roadmap, not switched after
				// briefly rendering the default (roadmap).
				options={launchMapOptions}
			>
				<AdvancedMarker position={property.location} title={property.name || property.addressLine1 || "Property"} />
				<Circle center={property.location} radius={radiusMeters} options={CIRCLE_OPTIONS} />
			</GoogleMap>
		);
	};

	const renderLaunchStep = () => {
		const customRadiusFt = parseInt(state.radius, 10) || 0;
		const radiusValid = customRadiusFt > 0;
		const radiusMeters = customRadiusFt * 0.3048;
		return (
			<div className="unitMiningLaunchGrid">
				<div>
					<div className="unitMiningFieldLabel">Base address</div>
					<div style={{ fontSize: 18, marginTop: 6, lineHeight: 1.4 }}>
						{property.addressLine1 || "No address on file"}
						<br />
						{[property.city, property.state].filter(Boolean).join(", ")}
						{property.zip ? ` ${property.zip}` : ""}
					</div>

					<div className="unitMiningFieldLabel" style={{ marginTop: 24 }}>Property types</div>
					<div className="unitMiningHint">All selected types are combined into a single radius search.</div>
					{/* A pill-toggle grid stopped scaling once a 5th, longer-label type
					    (Single Family Residence) joined the original 3 — labels wrapped
					    unevenly and, since grid row items stretch to match their
					    tallest row-mate, dragged short buttons like Apartment/Condo
					    up to match. Same Autocomplete multi-select + checkbox-option
					    pattern as the site's other multi-selects (see the checkbox
					    filterType branch in TanStackTableFilterPopover.js) — chips in
					    the input, a checkbox per dropdown option — so this looks and
					    behaves like every other "pick several of these" field here,
					    not a one-off. */}
					<Autocomplete
						multiple
						size="small"
						sx={{ mt: 1.5 }}
						value={state.types}
						onChange={(e, value) => dispatch({ type: "SET_TYPES", value })}
						options={PROPERTY_TYPES}
						getOptionLabel={(option) => option}
						disableCloseOnSelect
						renderOption={(props, option, { selected }) => {
							const { key, ...optionProps } = props;
							const Icon = TYPE_ICON[option];
							return (
								<li key={key} {...optionProps}>
									<Checkbox
										icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
										checkedIcon={<CheckBoxIcon fontSize="small" />}
										style={{ marginRight: 8 }}
										checked={selected}
									/>
									<Icon fontSize="small" style={{ marginRight: 8, color: "rgba(0,0,0,.54)" }} />
									{option}
								</li>
							);
						}}
						renderTags={(tagValue, getTagProps) =>
							tagValue.map((option, index) => {
								const { key, ...tagProps } = getTagProps({ index });
								return <Chip key={key} label={option} size="small" {...tagProps} />;
							})
						}
						renderInput={(params) => <TextField {...params} placeholder={state.types.length ? "" : "Select property types"} />}
					/>

					<div className="unitMiningFieldLabel" style={{ marginTop: 24 }}>Search radius</div>
					<ToggleButtonGroup
						exclusive
						value={customRadiusMode ? "custom" : state.radius}
						onChange={(e, value) => {
							if (!value) return;
							if (value === "custom") {
								setCustomRadiusMode(true);
							} else {
								setCustomRadiusMode(false);
								dispatch({ type: "SET_RADIUS", value });
							}
						}}
						color="primary"
						size="small"
						sx={{ mt: 1.5, flexWrap: "wrap", gap: 1, "& .MuiToggleButtonGroup-grouped": { borderRadius: "999px !important", border: "1px solid rgba(0,0,0,.2) !important", ml: "0 !important" } }}
					>
						{RADIUS_OPTIONS.map((r) => (
							<ToggleButton key={r} value={r} sx={{ textTransform: "none" }}>
								{r}
							</ToggleButton>
						))}
						<ToggleButton value="custom" sx={{ textTransform: "none" }}>
							Custom
						</ToggleButton>
					</ToggleButtonGroup>
					{customRadiusMode && (
						<TextField
							size="small"
							type="number"
							label="Custom radius (ft)"
							value={state.radius.replace(/\s*ft\s*$/, "")}
							onChange={(ev) => dispatch({ type: "SET_RADIUS", value: `${ev.target.value} ft` })}
							inputProps={{ min: 1, step: 1 }}
							sx={{ mt: 1.5, width: 180 }}
						/>
					)}
					{customRadiusMode && customRadiusFt > CUSTOM_RADIUS_WARNING_THRESHOLD_FT && (
						<Alert severity="warning" sx={{ mt: 1.5 }}>
							That's a large radius. Expect a lot more candidates to review, and more provider credits used per search.
						</Alert>
					)}
					<div className="unitMiningHint">Measured from the base address, drawn on the map.</div>

					<div style={{ marginTop: 24 }}>
						<Button variant="contained" size="medium" disabled={state.types.length === 0 || !radiusValid} onClick={runDiscovery} startIcon={<PlayArrowIcon fontSize="small" />}>
							Run Discovery
						</Button>
					</div>
				</div>

				<div>
					<Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1.5} sx={{ mb: 1 }}>
						{gmapsUri && (
							<a href={gmapsUri} target="_blank" rel="noopener noreferrer" className="propertyViewerTableLink">
								<PlaceIcon fontSize="inherit" /> Google Maps
							</a>
						)}
						<ToggleButtonGroup value={mapMode} exclusive size="small" color="primary" onChange={(e, val) => { if (val) setMapMode(val); }}>
							<ToggleButton value="m">Map</ToggleButton>
							<ToggleButton value="k">Satellite</ToggleButton>
						</ToggleButtonGroup>
					</Stack>
					<div className="unitMiningMapFrame">
						{!launchMapDialogOpen && renderLaunchMap(radiusMeters)}
						<IconButton
							size="small"
							className="unitMiningMapExpandButton"
							onClick={() => setLaunchMapDialogOpen(true)}
							aria-label="Expand map"
							title="Expand map"
						>
							<FullscreenIcon fontSize="small" />
						</IconButton>
					</div>
					<div className="unitMiningMapCaption">
						<span>{property.addressLine1 ? `${property.addressLine1} · ${state.radius} radius (${Math.round(radiusMeters)} m)` : "No address on file"}</span>
						<span className="unitMiningMapLegend">
							<span className="unitMiningMapLegendDot" />
							Search area
						</span>
					</div>

					<Dialog
						open={launchMapDialogOpen}
						onClose={() => setLaunchMapDialogOpen(false)}
						fullWidth
						maxWidth="lg"
						PaperProps={{ className: "unitMiningMapDialogPaper" }}
					>
						<div className="unitMiningMapDialogHeader">
							<h3>{property.addressLine1 || "Search area"}</h3>
							<div className="unitMiningMapDialogActions">
								<ToggleButtonGroup value={mapMode} exclusive size="small" color="primary" onChange={(e, val) => { if (val) setMapMode(val); }}>
									<ToggleButton value="m">Map</ToggleButton>
									<ToggleButton value="k">Satellite</ToggleButton>
								</ToggleButtonGroup>
								<IconButton onClick={() => setLaunchMapDialogOpen(false)} aria-label="Close">
									<CloseIcon />
								</IconButton>
							</div>
						</div>
						<div className="unitMiningMapDialogFrame">{launchMapDialogOpen && renderLaunchMap(radiusMeters)}</div>
					</Dialog>
				</div>
			</div>
		);
	};

	const renderDiscoveryMap = () => {
		if (mapsLoadError) return <div className="propertyViewerEmptyStateMessage">Failed to load Google Maps.</div>;
		if (!isMapsLoaded) return <div className="propertyViewerEmptyStateMessage">Loading map…</div>;
		if (!property.location) return <div className="propertyViewerEmptyStateMessage">No coordinates on file for this property.</div>;
		return (
			<GoogleMap
				mapContainerStyle={GOOGLE_MAP_CONTAINER_STYLE}
				center={property.location}
				zoom={17}
				mapTypeId={mapTypeId}
				onLoad={(map) => setDiscoveryMapInstance(map)}
				onClick={() => setActiveCandMarkerKey(null)}
				// See renderLaunchMap's comment — mapTypeId has to be baked into
				// options too or the map briefly creates as roadmap first.
				options={discoveryMapOptions}
			>
				<AdvancedMarker
					position={property.location}
					title={property.name || property.addressLine1 || "Property"}
					onClick={() => setActiveCandMarkerKey("property")}
				>
					{activeCandMarkerKey === "property" && (
						<InfoWindow onCloseClick={() => setActiveCandMarkerKey(null)}>
							<div>
								<strong>{property.name || "Property"}</strong>
								{property.addressLine1 && <div>{property.addressLine1}</div>}
							</div>
						</InfoWindow>
					)}
				</AdvancedMarker>
				{candMarkerGroups.map((group) => {
					const isCluster = group.cands.length > 1;
					const isSelectedOnMap = group.cands.some((c) => mapSelectedIds.has(c.id));
					const pinContent = buildUnitPinElement({
						color: candGroupColor(group),
						label: isCluster ? String(group.cands.length) : "",
						diameter: isCluster ? 26 : 18,
						selected: isSelectedOnMap,
					});
					return (
						<AdvancedMarker
							key={group.key}
							position={group.location}
							content={pinContent}
							title={isCluster ? `${group.cands.length} candidates` : `${group.cands[0].a1} ${group.cands[0].a2}`}
							onClick={() => (mapSelectMode ? toggleMapGroupSelection(group) : setActiveCandMarkerKey(group.key))}
						>
							{!mapSelectMode && activeCandMarkerKey === group.key && (
								<InfoWindow onCloseClick={() => setActiveCandMarkerKey(null)}>
									<div className="unitMiningMapInfoWindow">
										{group.cands.map((c) => (
											<div key={c.id} className="unitMiningMapInfoWindowCand">
												<div style={{ fontWeight: 600, fontSize: 13 }}>
													{c.a1} {c.a2}
													{c.flag && (
														<Tooltip title={c.flag} arrow>
															<WarningIcon
																fontSize="small"
																color="warning"
																style={{ verticalAlign: "middle", padding: 4, marginLeft: 4, marginTop: -4, marginRight: -4, marginBottom: -4 }}
															/>
														</Tooltip>
													)}
												</div>
												<div style={{ fontSize: 11, color: "rgba(0,0,0,.6)" }}>{c.csz}</div>
												<div style={{ textAlign: "center", marginTop: 6 }}>
													<ToggleButtonGroup
														exclusive
														size="small"
														value={c.decision}
														onChange={(e, value) => decideCand(c, value)}
														sx={{ "& .MuiToggleButton-root": { py: 0.25 } }}
													>
														<ToggleButton value="accept" title="Accept" color="success">
															<CheckIcon fontSize="small" />
														</ToggleButton>
														<ToggleButton value="uncertain" title="Uncertain" color="warning">
															<HelpOutlineIcon fontSize="small" />
														</ToggleButton>
														<ToggleButton value="reject" title="Reject" color="error">
															<CloseIcon fontSize="small" />
														</ToggleButton>
													</ToggleButtonGroup>
												</div>
											</div>
										))}
									</div>
								</InfoWindow>
							)}
						</AdvancedMarker>
					);
				})}
				{referenceMarkerGroups.map((group) => {
					const isCluster = group.units.length > 1;
					const pinContent = buildUnitPinElement({
						color: referenceGroupColor(group),
						label: isCluster ? String(group.units.length) : "",
						diameter: isCluster ? 22 : 14,
					});
					return (
						<AdvancedMarker
							key={group.key}
							position={group.location}
							content={pinContent}
							title={isCluster ? `${group.units.length} units already past Discovery` : `${group.units[0].a1} ${group.units[0].a2}`}
							onClick={() => setActiveCandMarkerKey(group.key)}
						>
							{activeCandMarkerKey === group.key && (
								<InfoWindow onCloseClick={() => setActiveCandMarkerKey(null)}>
									<div className="unitMiningMapInfoWindow">
										{group.units.map((u) => (
											<div key={u.id} className="unitMiningMapInfoWindowCand">
												<div style={{ fontWeight: 600, fontSize: 13 }}>
													{u.a1} {u.a2}
												</div>
												<div style={{ fontSize: 11, color: "rgba(0,0,0,.6)" }}>{u.csz}</div>
												<div style={{ marginTop: 4 }}>
													<Chip size="small" label={displayUnitStatus(u.status)} />
												</div>
											</div>
										))}
									</div>
								</InfoWindow>
							)}
						</AdvancedMarker>
					);
				})}
			</GoogleMap>
		);
	};

	// Shared by the inline Discovery card and its expanded map dialog — same
	// state either way, so multiselect/bulk-decide keeps working identically
	// regardless of which one is currently on screen.
	const renderMapSelectToolbar = () => (
		<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flexWrap="wrap" sx={{ mt: 1.5 }}>
			<Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
				<ToggleButton
					value="mapSelect"
					selected={mapSelectMode}
					onChange={toggleMapSelectMode}
					size="small"
					sx={{ textTransform: "none", gap: 0.75 }}
				>
					<HighlightAltIcon fontSize="small" />
					Multiselect
				</ToggleButton>
				{mapSelectMode && (
					<Stack direction="row" alignItems="center" spacing={1}>
						<span className="unitMiningSummary">{mapSelectedIds.size} selected on map</span>
						<ToggleButtonGroup size="small" sx={{ "& .MuiToggleButton-root": { py: 0.25, textTransform: "none" } }}>
							<ToggleButton value="accept" color="success" disabled={!mapSelectedIds.size} onClick={() => bulkDecideMapSelection("accept")}>
								Accept
							</ToggleButton>
							<ToggleButton value="uncertain" color="warning" disabled={!mapSelectedIds.size} onClick={() => bulkDecideMapSelection("uncertain")}>
								Uncertain
							</ToggleButton>
							<ToggleButton value="reject" color="error" disabled={!mapSelectedIds.size} onClick={() => bulkDecideMapSelection("reject")}>
								Reject
							</ToggleButton>
						</ToggleButtonGroup>
						<Button size="small" variant="text" disabled={!mapSelectedIds.size} onClick={() => setMapSelectedIds(new Set())}>
							Clear
						</Button>
					</Stack>
				)}
			</Stack>
			<Stack direction="row" alignItems="center" spacing={1.5}>
				{gmapsUri && (
					<a href={gmapsUri} target="_blank" rel="noopener noreferrer" className="propertyViewerTableLink">
						<PlaceIcon fontSize="inherit" /> Google Maps
					</a>
				)}
				<ToggleButtonGroup value={mapMode} exclusive size="small" color="primary" onChange={(e, val) => { if (val) setMapMode(val); }}>
					<ToggleButton value="m">Map</ToggleButton>
					<ToggleButton value="k">Satellite</ToggleButton>
				</ToggleButtonGroup>
			</Stack>
		</Stack>
	);

	// ---- step 2: discovery review ------------------------------------------
	const renderDiscoveryStep = () => {
		const q = state.candSearch.trim().toLowerCase();
		const rows = state.cands.filter((c) => !q || `${c.a1} ${c.a2} ${c.csz} ${c.type}`.toLowerCase().includes(q));
		if (candSortKey) rows.sort((a, b) => compareForSort(a[candSortKey], b[candSortKey]) * (candSortDir === "asc" ? 1 : -1));
		const selCount = state.cands.filter((c) => c.sel && c.decision !== "reject").length;
		const flagged = state.cands.filter((c) => c.flag).length;
		const acceptedCands = state.cands.filter((c) => c.decision === "accept");
		const acceptedAllSelected = acceptedCands.length > 0 && acceptedCands.every((c) => c.sel);
		// True right after a cancelled enrichment run reverts some units back
		// to this status with no enrich record yet — lets the same button
		// double as "resume" with nothing newly picked in Discovery Review.
		const hasResumableEnrichment = state.units.some((u) => u.status === "Ready for Enrichment");
		const pushedCount = state.units.filter((u) => u.status === "Pushed" || u.status === "Error").length;
		const enrichmentStageCount = state.units.length - pushedCount;
		const allChecked = rows.length > 0 && rows.every((c) => c.sel);
		const someChecked = !allChecked && rows.some((c) => c.sel);
		return (
			<>
				<div>
					<div style={{ fontSize: 16, fontWeight: 500 }}>Discovery</div>
					<div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,.6)" }}>
						Proximity search returns addresses near the property, including neighboring buildings. Confirm each candidate is a unit at{" "}
						{property.addressLine1 || "this property"} before loading it.
					</div>
				</div>

				{renderMapSelectToolbar()}

				<div className="unitMiningMapFrame" style={{ marginTop: 8 }}>
					{!discoveryMapDialogOpen && renderDiscoveryMap()}
					<IconButton
						size="small"
						className="unitMiningMapExpandButton"
						onClick={() => setDiscoveryMapDialogOpen(true)}
						aria-label="Expand map"
						title="Expand map"
					>
						<FullscreenIcon fontSize="small" />
					</IconButton>
				</div>

				<Dialog
					open={discoveryMapDialogOpen}
					onClose={() => setDiscoveryMapDialogOpen(false)}
					fullWidth
					maxWidth="lg"
					PaperProps={{ className: "unitMiningMapDialogPaper" }}
				>
					<div className="unitMiningMapDialogHeader">
						<h3>{property.addressLine1 || "Discovery"}</h3>
						<IconButton onClick={() => setDiscoveryMapDialogOpen(false)} aria-label="Close">
							<CloseIcon />
						</IconButton>
					</div>
					<div className="unitMiningMapDialogToolbar">{renderMapSelectToolbar()}</div>
					<div className="unitMiningMapDialogFrame">{discoveryMapDialogOpen && renderDiscoveryMap()}</div>
				</Dialog>

				{state.units.length > 0 && (
					<Alert severity="info" sx={{ mt: 2 }}>
						{[
							enrichmentStageCount > 0 && `${enrichmentStageCount} unit${enrichmentStageCount === 1 ? "" : "s"} in enrichment`,
							pushedCount > 0 && `${pushedCount} already pushed to HubSpot`,
						]
							.filter(Boolean)
							.join(" and ")}{" "}
						from this property. Sending more here adds to that set. It won't affect what's already there. Those are also shown on the map for
						reference (with a muted pin), just not in the table.
					</Alert>
				)}

				<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flexWrap="wrap" className="unitMiningBulkBar">
					<Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap">
						<TextField
							size="small"
							placeholder="Search candidates"
							value={state.candSearch}
							onChange={(e) => dispatch({ type: "SET_CAND_SEARCH", value: e.target.value })}
							sx={{ width: 220 }}
						/>
						<Button variant="outlined" size="small" onClick={toggleSelectAccepted}>
						{acceptedAllSelected ? "Deselect all accepted" : "Select all accepted"}
					</Button>
					</Stack>
					<Stack direction="row" alignItems="center" spacing={1.5}>
						<span className="unitMiningSummary">{selCount} selected · {flagged} flagged · {state.cands.length} found</span>
						<Button
							variant="contained"
							size="small"
							disabled={(selCount === 0 && !hasResumableEnrichment) || loadSelectedPending}
							onClick={doLoadSelected}
							startIcon={loadSelectedPending ? <CircularProgress size={14} color="inherit" /> : <PersonSearchIcon fontSize="small" />}
						>
							{loadSelectedPending ? "Sending…" : "Send to Enrichment"}
						</Button>
					</Stack>
				</Stack>

				<div style={{ marginTop: 14, overflowX: "auto" }}>
					<table className="propertyViewerTable unitMiningReviewTable">
						<thead>
							<tr>
								<th style={{ width: 38, textAlign: "center" }}>
									<Checkbox
										size="small"
										checked={allChecked}
										indeterminate={someChecked}
										onChange={() => setCandSelMany(rows.map((c) => c.id), !allChecked)}
									/>
								</th>
								<th className="unitMiningSortableHeader" onClick={() => handleCandSort("a1")}>
									<div className="unitMiningSortableHeaderLabel">
										Address 1
										{renderSortIcon(candSortKey === "a1", candSortDir)}
									</div>
								</th>
								<th style={{ width: 120 }} className="unitMiningSortableHeader" onClick={() => handleCandSort("a2")}>
									<div className="unitMiningSortableHeaderLabel">
										Address 2
										{renderSortIcon(candSortKey === "a2", candSortDir)}
									</div>
								</th>
								<th style={{ width: 190 }} className="unitMiningSortableHeader" onClick={() => handleCandSort("csz")}>
									<div className="unitMiningSortableHeaderLabel">
										City / State / Zip
										{renderSortIcon(candSortKey === "csz", candSortDir)}
									</div>
								</th>
								<th style={{ width: 260 }} className="unitMiningSortableHeader" onClick={() => handleCandSort("flag")}>
									<div className="unitMiningSortableHeaderLabel">
										Plausibility
										{renderSortIcon(candSortKey === "flag", candSortDir)}
									</div>
								</th>
								<th style={{ width: 140 }} className="unitMiningSortableHeader" onClick={() => handleCandSort("decision")}>
									<div className="unitMiningSortableHeaderLabel">
										Decision
										{renderSortIcon(candSortKey === "decision", candSortDir)}
									</div>
								</th>
								<th style={{ width: 44 }} />
							</tr>
						</thead>
						<tbody>
							{rows.map((c) => (
								<tr
								key={c.id}
								className={
									c.decision === "reject"
										? "unitMiningRowRejected"
										: c.decision === "accept"
											? "unitMiningRowAccepted"
											: c.decision === "uncertain"
												? "unitMiningRowUncertain"
												: undefined
								}
							>
									<td style={{ textAlign: "center" }}>
										<Checkbox size="small" checked={c.sel} onChange={() => toggleCand(c)} />
									</td>
									<td>{c.a1}</td>
									<td>{c.a2}</td>
									<td>{c.csz}</td>
									<td>
										{c.flag ? (
											<div className="unitMiningFlagCell">
												<WarningIcon fontSize="small" color="warning" />
												{c.flag}
											</div>
										) : (
											<span className="unitMiningOkCell">Matches base property</span>
										)}
									</td>
									<td>
										<ToggleButtonGroup
											exclusive
											size="small"
											value={c.decision}
											onChange={(e, value) => decideCand(c, value)}
										>
											<ToggleButton value="accept" title="Accept" color="success">
												<CheckIcon fontSize="small" />
											</ToggleButton>
											<ToggleButton value="uncertain" title="Uncertain" color="warning">
												<HelpOutlineIcon fontSize="small" />
											</ToggleButton>
											<ToggleButton value="reject" title="Reject" color="error">
												<CloseIcon fontSize="small" />
											</ToggleButton>
										</ToggleButtonGroup>
									</td>
									<td style={{ textAlign: "center" }}>
										<IconButton size="small" title="View raw provider JSON" onClick={() => dispatch({ type: "OPEN_JSON", id: c.id })}>
											<DataObjectIcon fontSize="small" />
										</IconButton>
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</>
		);
	};

	// ---- step 3: enrichment review ------------------------------------------
	const renderEnrichmentStep = () => {
		const rows = state.enrich.filter((e) => matchesOutcomeFilter(e, state.outcomeFilter));
		const toPushDisabled = !state.enrich.some((e) => e.approved);
		const approvedCount = state.enrich.filter((e) => e.approved).length;
		const conflictCount = state.enrich.filter((e) => e.outcome === "Address conflict" && !e.approved).length;
		// Not gated on !e.approved like conflictCount above — a row can be marked
		// ready and still carry this flag (it's advisory, not a hard block), and
		// that's still worth surfacing here rather than quietly dropping out of
		// the count once approved. It IS gated on pdlLookupAttempted (via
		// needsAddressReviewOutstanding) so this count visibly goes down as
		// someone actually works through the flagged rows, not just when the
		// address changes.
		const needsAddressReviewCount = state.enrich.filter(needsAddressReviewOutstanding).length;
		return (
			<>
				<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2.5} flexWrap="wrap">
					<div style={{ fontSize: 16, fontWeight: 500 }}>Enrichment</div>
					<Stack direction="row" alignItems="center" spacing={1.25}>
						<Button variant="outlined" size="small" onClick={approveConfident} startIcon={<DoneAllIcon fontSize="small" />}>
							Approve confident matches
						</Button>
						<Button variant="contained" size="small" disabled={toPushDisabled} onClick={() => dispatch({ type: "GO_PUSH" })}>
							Continue to Push
						</Button>
					</Stack>
				</Stack>
				<div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,.6)" }}>
					Owner and resident records returned by the provider. Every field is editable; conflicts need an explicit resolution before a unit can be marked
					ready.
				</div>

				{state.enrich.length > 0 && (
					<div style={{ marginTop: 16 }}>
						<Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={2} flexWrap="wrap">
							<div style={{ fontSize: 12, color: "rgba(0,0,0,.6)" }}>
								{approvedCount} of {state.enrich.length} marked ready
								{conflictCount > 0 && (
									<span style={{ color: "#b53943", fontWeight: 500 }}>
										{" "}
										· {conflictCount} unresolved conflict{conflictCount === 1 ? "" : "s"}
									</span>
								)}
								{needsAddressReviewCount > 0 && (
									<span style={{ color: "#c98a1f", fontWeight: 500 }}>
										{" "}
										· {needsAddressReviewCount} needing address review
									</span>
								)}
							</div>
						</Stack>
						<LinearProgress
							variant="determinate"
							color={conflictCount > 0 || needsAddressReviewCount > 0 ? "warning" : "success"}
							value={(approvedCount / state.enrich.length) * 100}
							sx={{ mt: 0.75 }}
						/>
					</div>
				)}

				{state.pushRan && (
					<Alert severity="warning" sx={{ mt: 2 }}>
						Units already pushed to HubSpot are shown for reference. Edits here do not reach records that have been written.
					</Alert>
				)}

				<ToggleButtonGroup
					exclusive
					size="small"
					color="primary"
					value={state.outcomeFilter}
					onChange={(e, value) => { if (value) dispatch({ type: "PICK_OUTCOME", value }); }}
					sx={{ mt: 2, flexWrap: "wrap", gap: 1, "& .MuiToggleButtonGroup-grouped": { borderRadius: "999px !important", border: "1px solid rgba(0,0,0,.2) !important", ml: "0 !important" } }}
				>
					{OUTCOME_LABELS.map((l) => {
						const count = l === "All" ? state.enrich.length : state.enrich.filter((e) => matchesOutcomeFilter(e, l)).length;
						return (
							<ToggleButton key={l} value={l} sx={{ textTransform: "none" }}>
								{l} ({count})
							</ToggleButton>
						);
					})}
				</ToggleButtonGroup>

				{rows.length === 0 && (
					<Alert severity="info" sx={{ mt: 2 }}>
						No records match this filter.
					</Alert>
				)}

				<Stack spacing={1.5} sx={{ mt: 2 }}>
					{rows.map((e) => (
						<div
							key={e.id}
							className={`propertyViewerPaper unitMiningEnrichCard${
								(e.outcome === "Address conflict" || e.secondOwnerOutcome === "Address conflict") && !e.approved
									? " unitMiningEnrichCardConflict"
									: e.approved
										? " unitMiningEnrichCardApproved"
										: needsAddressReviewOutstanding(e)
											? " unitMiningEnrichCardNeedsAddressReview"
											: ""
							}`}
						>
							<div className="propertyViewerPaperHeader" style={{ flexWrap: "wrap", rowGap: 10 }}>
								<Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" sx={{ rowGap: 1 }}>
									<span style={{ fontSize: 14, fontWeight: 500 }}>{e.unit}</span>
									<span style={{ fontSize: 13, color: "rgba(0,0,0,.6)" }}>{property.addressLine1}</span>
									<Chip size="small" label={e.outcome} color={OUTCOME_CHIP_COLOR[e.outcome] || "default"} />
									{e.occupancy && <Chip size="small" label={e.occupancy} />}
									{needsAddressReviewOutstanding(e) && <Chip size="small" label="Needs address review" color="warning" />}
								</Stack>
								<Stack direction="row" alignItems="center" spacing={1}>
									<IconButton size="small" title="Raw provider JSON" onClick={() => dispatch({ type: "OPEN_JSON", id: e.id })}>
										<DataObjectIcon fontSize="small" />
									</IconButton>
									{e.outcome === "Address conflict" && (
										<Button
											variant="outlined"
											color="error"
											size="small"
											onClick={() => dispatch({ type: "OPEN_CONFLICT", id: e.id, person: "primary" })}
											startIcon={<CompareArrowsIcon fontSize="small" />}
										>
											Resolve conflict
										</Button>
									)}
									<Button
										size="small"
										variant={e.approved ? "contained" : "outlined"}
										color="success"
										onClick={() => approveEnrich(e.id)}
										startIcon={e.approved ? <CheckCircleIcon fontSize="small" /> : <CheckIcon fontSize="small" />}
									>
										{e.approved ? "Ready" : "Mark ready"}
									</Button>
								</Stack>
							</div>
							<div className="propertyViewerPaperBody">
								<div className="unitMiningEnrichGrid">
									{e.kind === "corporate" ? (
										<TextField
											size="small"
											fullWidth
											label="Entity name"
											value={e.name}
											onChange={(ev) => dispatch({ type: "EDIT_ENRICH", id: e.id, field: "name", value: ev.target.value })}
											onBlur={() => persistEnrichmentRecord(state.enrich.find((r) => r.id === e.id))}
										/>
									) : (
										<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
											<TextField
												size="small"
												fullWidth
												label="First name"
												value={primaryOwnerNameParts(e).firstName}
												onChange={(ev) => dispatch({ type: "EDIT_OWNER_NAME", id: e.id, part: "firstName", value: ev.target.value })}
												onBlur={() => persistEnrichmentRecord(state.enrich.find((r) => r.id === e.id))}
											/>
											<TextField
												size="small"
												fullWidth
												label="Last name"
												value={primaryOwnerNameParts(e).lastName}
												onChange={(ev) => dispatch({ type: "EDIT_OWNER_NAME", id: e.id, part: "lastName", value: ev.target.value })}
												onBlur={() => persistEnrichmentRecord(state.enrich.find((r) => r.id === e.id))}
											/>
										</div>
									)}
									<TextField
										size="small"
										fullWidth
										label="Mailing address"
										value={e.mail1}
										onChange={(ev) => dispatch({ type: "EDIT_ENRICH", id: e.id, field: "mail1", value: ev.target.value })}
										onBlur={() => persistEnrichmentRecord(state.enrich.find((r) => r.id === e.id))}
									/>
									<TextField
										size="small"
										fullWidth
										label="City / State / Zip"
										value={e.mail2}
										onChange={(ev) => dispatch({ type: "EDIT_ENRICH", id: e.id, field: "mail2", value: ev.target.value })}
										onBlur={() => persistEnrichmentRecord(state.enrich.find((r) => r.id === e.id))}
									/>
									{e.mailMatchesPropertyAddress && (
										<div className={`unitMiningPdlNotice${e.pdlLookupAttempted ? " unitMiningPdlNoticeReviewed" : ""}`}>
											<span>
												{e.pdlLookupAttempted ? (
													"Already looked up once. Click below to look up again if needed."
												) : (
													<>
														This owner is absentee, but their mailing address is this unit's own address here at the complex.
														It's probably not their real address.
													</>
												)}
											</span>
											<Button
												size="small"
												variant="outlined"
												onClick={() => runPdlLookup(e.id)}
												startIcon={<PersonSearchIcon fontSize="small" />}
											>
												{e.pdlLookupAttempted ? "Look up again" : "Look up real address"}
											</Button>
										</div>
									)}
									<TextField
										size="small"
										fullWidth
										label="Company"
										placeholder="—"
										value={e.company}
										onChange={(ev) => dispatch({ type: "EDIT_ENRICH", id: e.id, field: "company", value: ev.target.value })}
										onBlur={() => persistEnrichmentRecord(state.enrich.find((r) => r.id === e.id))}
									/>
									<div>
										<div className="unitMiningFieldLabel">Contact type</div>
										<ToggleButtonGroup
											exclusive
											fullWidth
											size="small"
											color="primary"
											value={e.kind}
											onChange={(ev, value) => { if (value) setEnrichKind(e.id, value); }}
											sx={{ mt: 0.75 }}
										>
											<ToggleButton value="individual" sx={{ textTransform: "none", gap: 0.5 }}>
												<PersonIcon fontSize="small" /> Individual
											</ToggleButton>
											<ToggleButton value="corporate" sx={{ textTransform: "none", gap: 0.5 }}>
												<BusinessIcon fontSize="small" /> Corporate
											</ToggleButton>
										</ToggleButtonGroup>
									</div>
									<div>
										<div className="unitMiningFieldLabel">Role</div>
										<ToggleButtonGroup
											exclusive
											fullWidth
											size="small"
											color="primary"
											value={e.role}
											onChange={(ev, value) => { if (value) setEnrichRole(e.id, value); }}
											sx={{ mt: 0.75 }}
										>
											<ToggleButton value="Owner" sx={{ textTransform: "none" }}>Owner</ToggleButton>
											<ToggleButton value="Resident" sx={{ textTransform: "none" }}>Resident</ToggleButton>
										</ToggleButtonGroup>
									</div>
									{hasSecondOwner(e) && (
										<>
											{/* A real, additional row in the SAME grid instance as the
											    fields above — not a lookalike copy in a separate grid — so its
											    columns are guaranteed to align with the row above; a parallel
											    grid claiming the same column widths doesn't actually guarantee
											    pixel alignment the way sharing one grid does. This divider cell
											    spans the full row (gridColumn 1/-1) to separate it from the
											    primary owner above. */}
											<div className="unitMiningEnrichCoOwnerDivider">
												<Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ rowGap: 1 }}>
													<Stack direction="row" alignItems="center" spacing={1}>
														<div className="unitMiningFieldLabel">Co-owner (split from trust name)</div>
														{e.secondOwnerOutcome && (
															<Chip size="small" label={e.secondOwnerOutcome} color={OUTCOME_CHIP_COLOR[e.secondOwnerOutcome] || "default"} />
														)}
													</Stack>
													<Stack direction="row" alignItems="center" spacing={1}>
														{e.secondOwnerOutcome === "Address conflict" && (
															<Button
																variant="outlined"
																color="error"
																size="small"
																onClick={() => dispatch({ type: "OPEN_CONFLICT", id: e.id, person: "second" })}
																startIcon={<CompareArrowsIcon fontSize="small" />}
															>
																Resolve conflict
															</Button>
														)}
														<Button size="small" onClick={() => removeSecondOwner(e.id)} startIcon={<CloseIcon fontSize="small" />}>
															Remove co-owner
														</Button>
													</Stack>
												</Stack>
												<div className="unitMiningHint" style={{ marginTop: 2 }}>
													Uses the mailing address above. Split co-owners share the same deed record.
												</div>
											</div>
											<TextField
												size="small"
												fullWidth
												label="First name"
												value={secondOwnerNameParts(e).firstName}
												onChange={(ev) => dispatch({ type: "EDIT_OWNER_NAME", id: e.id, second: true, part: "firstName", value: ev.target.value })}
												onBlur={() => persistEnrichmentRecord(state.enrich.find((r) => r.id === e.id))}
											/>
											<TextField
												size="small"
												fullWidth
												label="Last name"
												value={secondOwnerNameParts(e).lastName}
												onChange={(ev) => dispatch({ type: "EDIT_OWNER_NAME", id: e.id, second: true, part: "lastName", value: ev.target.value })}
												onBlur={() => persistEnrichmentRecord(state.enrich.find((r) => r.id === e.id))}
											/>
										</>
									)}
								</div>
							</div>
					</div>
					))}
				</Stack>
			</>
		);
	};

	// ---- step 4: push -------------------------------------------------------
	const renderPushStep = () => {
		const readyRows = state.units.filter((u) => ["Ready", "Pushed", "Error"].includes(u.status));
		const readyCount = state.units.filter((u) => u.status === "Ready").length;
		const failures = Object.values(state.pushResults).filter((r) => r.res === "Error").length;
		const pushedCount = Object.values(state.pushResults).filter((r) => r.res === "Pushed").length;
		const pushDisabled = readyCount === 0 || (state.pushRan && failures === 0);
		const canPushMore = !state.pushRan || readyCount > 0;
		const pushingNow = state.job && state.job.kind === "push";

		return (
			<>
				<Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flexWrap="wrap">
					<div style={{ fontSize: 16, fontWeight: 500 }}>Push to HubSpot</div>
					<Stack direction="row" alignItems="center" spacing={1.25}>
						<Button
							variant="outlined"
							size="medium"
							disabled={readyCount === 0 || pushingNow}
							onClick={openPreview}
							startIcon={<DataObjectIcon fontSize="small" />}
						>
							Preview payload
						</Button>
						{failures > 0 && !pushingNow && (
							<Button variant="outlined" color="error" size="small" onClick={retryFailed} startIcon={<RefreshIcon fontSize="small" />}>
								Retry failed
							</Button>
						)}
						{canPushMore && (
							<Button
								variant="contained"
								size="medium"
								disabled={pushDisabled || pushingNow}
								onClick={() => dispatch({ type: "OPEN_CONFIRM" })}
								startIcon={<CloudUploadIcon fontSize="small" />}
							>
								{state.pushRan ? "Push remaining" : `Push ${readyCount} Units`}
							</Button>
						)}
						{state.pushRan && (
							<Button variant="contained" color="success" size="medium" disabled={pushingNow || failures > 0} onClick={finishRun} startIcon={<DoneAllIcon fontSize="small" />}>
								Finish
							</Button>
						)}
					</Stack>
				</Stack>
				<div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,.6)" }}>
					Only units marked ready are included. This writes to a shared external system and cannot be undone from here.
				</div>

				{state.pushRan && !pushingNow && (
					<Alert severity={failures ? "warning" : "success"} sx={{ mt: 2 }}>
						{pushedCount} units pushed{failures ? ` · ${failures} failed, retry before finishing` : " · press Finish to close mining for this property"}
					</Alert>
				)}

				{pushingNow && (
					<div style={{ marginTop: 16 }}>
						<Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={2} flexWrap="wrap">
							<Stack direction="row" alignItems="center" spacing={1.25} sx={{ fontSize: 13, fontWeight: 500 }}>
								<AutorenewIcon fontSize="small" color="primary" className="unitMiningJobIcon spin" />
								{state.retrying ? "Retrying failed units" : "Pushing units and contact associations"}
							</Stack>
							<div style={{ fontSize: 12, color: "rgba(0,0,0,.6)", fontVariantNumeric: "tabular-nums" }}>
								{state.job.done} of {state.job.items.length} · {state.job.calls} provider calls · {(state.job.elapsedMs / 1000).toFixed(1)}s
							</div>
						</Stack>
						<LinearProgress
							variant="determinate"
							value={state.job.items.length ? (state.job.done / state.job.items.length) * 100 : 0}
							sx={{ mt: 1.5 }}
						/>
						<div style={{ marginTop: 10, fontSize: 12, color: "rgba(0,0,0,.5)" }}>
							This run writes to the shared production portal and cannot be cancelled. Failures can be retried afterwards.
						</div>
					</div>
				)}

				<div style={{ marginTop: 16 }}>
					<div style={{ overflowX: "auto" }}>
						<table className="propertyViewerTable">
							<thead>
								<tr>
									<th>Unit</th>
									<th>Contact to associate</th>
									<th style={{ width: 210 }}>Result</th>
								</tr>
							</thead>
							<tbody>
								{readyRows.map((u, i) => {
									// state.pushResults[u.id] is updated in real time by
									// RECORD_PUSH_RESULT as each unit's real push call
									// completes — the only thing left to compute here is
									// which row is the one currently in flight.
									let res = state.pushResults[u.id];
									if (!res && pushingNow && i === state.job.done) res = { res: "Pushing" };
									const resultColor = res ? (res.res === "Error" ? "error" : res.res === "Pushing" ? "primary" : "success") : "default";
									return (
										<tr key={u.id}>
											<td style={{ fontWeight: 500 }}>
												{u.a2} · {u.a1}
											</td>
											<td style={{ color: "rgba(0,0,0,.6)" }}>{u.owner || "—"}</td>
											<td>
												<Stack direction="row" alignItems="center" spacing={1}>
													<Chip size="small" color={resultColor} label={res ? res.res : "Pending"} />
													{res?.error && <span className="unitMiningResultError">{res.error}</span>}
												</Stack>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
					<div className="unitMiningPushScope">
						{readyCount} unit{readyCount === 1 ? "" : "s"} will be sent to the J2 production HubSpot portal, each associated with its owner. A
						unit or contact already in HubSpot is matched and updated rather than duplicated. Use Preview payload to see exactly which for
						each one.
					</div>
				</div>
			</>
		);
	};

	// ---- dialogs -------------------------------------------------------------
	// "pdl-lookup" is a sentinel jsonId (never a real cand/unit/enrich id,
	// which are always numeric or "e_<unitId>") for viewing the raw People
	// Data Labs response opened from inside the lookup dialog above — reuses
	// this same JSON-viewer dialog rather than building a second one, and
	// stacks on top of the still-open lookup dialog since CLOSE_MODALS only
	// clears jsonId, not pdlLookupId.
	const isPdlJson = state.jsonId === "pdl-lookup";
	const jsonRec = isPdlJson
		? null
		: state.cands.find((c) => c.id === state.jsonId) || state.units.find((u) => u.id === state.jsonId) || state.enrich.find((e) => e.id === state.jsonId);
	const jsonDialogOpen = isPdlJson || !!jsonRec;
	// The exact ATTOM (or, for the PDL case, People Data Labs) response
	// captured — attached server-side at discovery/enrichment/lookup time.
	// Units enriched before that was wired up won't have one on file.
	const jsonBody = isPdlJson
		? state.pdlLookupResult?.raw
			? JSON.stringify(state.pdlLookupResult.raw, null, 2)
			: ""
		: jsonRec
			? jsonRec.providerRawJson
				? JSON.stringify(jsonRec.providerRawJson, null, 2)
				: ""
			: "";

	const renderJsonDialog = () => (
		<Dialog open={jsonDialogOpen} onClose={() => dispatch({ type: "CLOSE_MODALS" })} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
				<div>
					<div>{isPdlJson ? "Raw People Data Labs response" : "Raw provider response"}</div>
				</div>
				<IconButton size="small" onClick={() => dispatch({ type: "CLOSE_MODALS" })}>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent>
				{jsonBody ? (
					<pre
						style={{
							margin: 0,
							background: "#f7f8fa",
							border: "1px solid rgba(0,0,0,.12)",
							borderRadius: 6,
							padding: 14,
							fontSize: 12,
							lineHeight: 1.6,
							overflow: "auto",
							maxHeight: "52vh",
							fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
						}}
					>
						{jsonBody}
					</pre>
				) : (
					<Alert severity="info">
						{isPdlJson ? "No raw People Data Labs response is available." : "No raw provider response was captured for this unit."}
					</Alert>
				)}
			</DialogContent>
		</Dialog>
	);

	const conflictRec = state.enrich.find((e) => e.id === state.conflictId);
	// The co-owner (split-trust second owner) shares the primary owner's
	// mailing address record — only the name and conflict-match fields are
	// ever "secondOwner*"-prefixed — so mail1/mail2 below are read straight
	// off conflictRec regardless of which owner's dialog this is.
	const conflictIsSecond = state.conflictPerson === "second";
	const conflictName = conflictIsSecond ? conflictRec?.secondOwnerName : conflictRec?.name;
	const conflictOld = conflictIsSecond ? conflictRec?.secondOwnerConflictOld : conflictRec?.conflictOld;
	const conflictContactId = conflictIsSecond ? conflictRec?.secondOwnerConflictContactId : conflictRec?.conflictContactId;
	const conflictLastActivity = conflictIsSecond ? conflictRec?.secondOwnerConflictLastActivity : conflictRec?.conflictLastActivity;
	const renderConflictDialog = () => (
		<Dialog open={!!conflictRec} onClose={() => dispatch({ type: "CLOSE_MODALS" })} maxWidth="lg" fullWidth>
			<DialogTitle>
				<Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
					<div>Same name, different address</div>
					<IconButton size="small" onClick={() => dispatch({ type: "CLOSE_MODALS" })}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Stack>
				<div style={{ fontSize: 13, color: "rgba(0,0,0,.6)", marginTop: 4, fontWeight: 400 }}>
					{conflictRec
						? `${conflictIsSecond ? "The co-owner split from the trust name" : "Provider returned " + conflictName} for ${
								conflictRec.unit !== "—" ? `${conflictRec.unit} at ${property.addressLine1}` : property.addressLine1
						  }${conflictIsSecond ? ` was parsed as ${conflictName}` : ""}, but an existing HubSpot contact with that name has a different mailing address. Decide whether this is the same person.`
						: ""}
				</div>
			</DialogTitle>
			<DialogContent>
				<div className="unitMiningConflictGrid">
					<div className="unitMiningConflictPane">
						<div className="unitMiningConflictPaneHeader">
							<span>Existing HubSpot contact</span>
							{conflictContactId && (
								<a
									href={HUBSPOT_CONTACT_RECORD_URL(conflictContactId)}
									target="_blank"
									rel="noopener noreferrer"
									className="unitMiningConflictPaneHeaderLink"
								>
									View record <LaunchIcon sx={{ fontSize: 13 }} />
								</a>
							)}
						</div>
						<div className="unitMiningConflictPaneBody">
							<div>
								<div className="unitMiningConflictFieldLabel">Name</div>
								{conflictName}
							</div>
							<div>
								<div className="unitMiningConflictFieldLabel">Mailing address</div>
								{conflictOld || "—"}
							</div>
							<div>
								<div className="unitMiningConflictFieldLabel">Last updated in HubSpot</div>
								{formatConflictDate(conflictLastActivity) || "—"}
							</div>
						</div>
					</div>
					<div className="unitMiningConflictPane new">
						<div className="unitMiningConflictPaneHeader">Newly parsed record</div>
						<div className="unitMiningConflictPaneBody">
							<div>
								<div className="unitMiningConflictFieldLabel">Name</div>
								{conflictName}
							</div>
							<div>
								<div className="unitMiningConflictFieldLabel">Mailing address</div>
								{[conflictRec?.mail1, conflictRec?.mail2].filter(Boolean).join(", ") || "—"}
							</div>
							<div>
								<div className="unitMiningConflictFieldLabel">Source</div>
								Parsed from ATTOM deed record
							</div>
						</div>
					</div>
				</div>
			</DialogContent>
			<DialogActions sx={{ flexWrap: "nowrap", gap: 1, justifyContent: "flex-end" }}>
				<Button variant="text" onClick={() => dispatch({ type: "CLOSE_MODALS" })} sx={{ mr: "auto", whiteSpace: "nowrap" }}>
					Cancel
				</Button>
				<Button variant="outlined" onClick={resolveNewPerson} sx={{ whiteSpace: "nowrap" }}>
					Different person, create new
				</Button>
				<Button variant="outlined" onClick={resolveSamePersonKeepAddress} sx={{ whiteSpace: "nowrap" }}>
					Same person, keep old address
				</Button>
				<Button variant="contained" onClick={resolveSamePerson} sx={{ whiteSpace: "nowrap" }}>
					Same person, update address
				</Button>
			</DialogActions>
		</Dialog>
	);

	// Same two-pane compare/accept-or-discard shape as the address-conflict
	// dialog above, for the People Data Labs "real address" candidate — never
	// applied automatically, low-confidence included, since this is paid,
	// name-only, and has no way to be certain it's the same person.
	const pdlLookupRec = state.enrich.find((e) => e.id === state.pdlLookupId);
	const renderPdlLookupDialog = () => (
		<Dialog open={!!state.pdlLookupId} onClose={() => dispatch({ type: "PDL_LOOKUP_CLOSE" })} maxWidth="lg" fullWidth>
			<DialogTitle>
				<Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
					<div>Look up real address</div>
					<Stack direction="row" spacing={0.5}>
						{state.pdlLookupStatus === "result" && (
							<IconButton size="small" title="Raw People Data Labs response" onClick={() => dispatch({ type: "OPEN_JSON", id: "pdl-lookup" })}>
								<DataObjectIcon fontSize="small" />
							</IconButton>
						)}
						<IconButton size="small" onClick={() => dispatch({ type: "PDL_LOOKUP_CLOSE" })}>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Stack>
				</Stack>
				<div style={{ fontSize: 13, color: "rgba(0,0,0,.6)", marginTop: 4, fontWeight: 400 }}>
					{pdlLookupRec ? `Searching People Data Labs for ${pdlLookupRec.name}'s real mailing address.` : ""}
				</div>
			</DialogTitle>
			<DialogContent>
				{state.pdlLookupStatus === "loading" && (
					<Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
						<CircularProgress size={28} />
						<div style={{ fontSize: 13, color: "rgba(0,0,0,.6)" }}>Searching People Data Labs…</div>
						<div style={{ fontSize: 12, color: "rgba(0,0,0,.45)" }}>This usually takes just a few seconds.</div>
					</Stack>
				)}
				{state.pdlLookupStatus === "error" && (
					<Alert severity="error" sx={{ mt: 1 }}>
						{state.pdlLookupError || "The lookup failed."}
					</Alert>
				)}
				{state.pdlLookupStatus === "result" && state.pdlLookupResult?.status === "not_found" && (
					<Alert severity="info" sx={{ mt: 1 }}>
						People Data Labs didn't find a match for this name. The mailing address on file is unchanged.
					</Alert>
				)}
				{state.pdlLookupStatus === "result" && (state.pdlLookupResult?.status === "found" || state.pdlLookupResult?.status === "low_confidence") && (
					<>
						{state.pdlLookupResult.status === "low_confidence" && (
							<Alert severity="warning" sx={{ mt: 1 }}>
								People Data Labs isn't very confident this is the same person. Review carefully before accepting.
							</Alert>
						)}
						<div className="unitMiningConflictGrid">
							<div className="unitMiningConflictPane">
								<div className="unitMiningConflictPaneHeader">Current mailing address</div>
								<div className="unitMiningConflictPaneBody">
									<div>
										<div className="unitMiningConflictFieldLabel">Address</div>
										{[pdlLookupRec?.mail1, pdlLookupRec?.mail2].filter(Boolean).join(", ") || "—"}
									</div>
									<div>
										<div className="unitMiningConflictFieldLabel">Source</div>
										Parsed from ATTOM deed record
									</div>
								</div>
							</div>
							<div className="unitMiningConflictPane new">
								<div className="unitMiningConflictPaneHeader">People Data Labs found</div>
								<div className="unitMiningConflictPaneBody">
									<div>
										<div className="unitMiningConflictFieldLabel">Address</div>
										{[state.pdlLookupResult.mail1, state.pdlLookupResult.mail2].filter(Boolean).join(", ") || "—"}
										{!state.pdlLookupResult.mail1 && (
											<div style={{ fontSize: 12, color: "rgba(0,0,0,.5)", marginTop: 2 }}>
												People Data Labs doesn't have a street address on file for this person, only a city/state. That's usually a
												sign it also isn't very sure this is the right person.
											</div>
										)}
									</div>
									<div>
										<div className="unitMiningConflictFieldLabel">Confidence this is the same person</div>
										{state.pdlLookupResult.likelihood != null ? `${state.pdlLookupResult.likelihood} / 10` : "—"}
										<div style={{ fontSize: 12, color: "rgba(0,0,0,.5)", marginTop: 2 }}>
											People Data Labs' own estimate that this profile is really{pdlLookupRec?.name ? ` ${pdlLookupRec.name}` : " this owner"}, not
											someone else with a similar name. It doesn't measure how complete or accurate the address itself is.
										</div>
									</div>
								</div>
							</div>
						</div>
					</>
				)}
			</DialogContent>
			<DialogActions sx={{ flexWrap: "nowrap", gap: 1, justifyContent: "flex-end" }}>
				<Button variant="text" onClick={() => dispatch({ type: "PDL_LOOKUP_CLOSE" })} sx={{ mr: "auto", whiteSpace: "nowrap" }}>
					{state.pdlLookupStatus === "result" && (state.pdlLookupResult?.status === "found" || state.pdlLookupResult?.status === "low_confidence")
						? "Discard, keep current address"
						: "Close"}
				</Button>
				{state.pdlLookupStatus === "result" && (state.pdlLookupResult?.status === "found" || state.pdlLookupResult?.status === "low_confidence") && (
					<Button variant="contained" onClick={acceptPdlLookup} sx={{ whiteSpace: "nowrap" }}>
						Use this address
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);

	const renderConfirmDialog = () => {
		const readyCount = state.units.filter((u) => u.status === "Ready").length;
		return (
			<Dialog open={state.confirmOpen} onClose={() => dispatch({ type: "CLOSE_MODALS" })} maxWidth="xs" fullWidth>
				<DialogTitle>Push {readyCount} units to HubSpot?</DialogTitle>
				<DialogContent>
					<Alert severity="warning">
						This writes to the shared production portal and cannot be undone from J2 Tools. Results appear per unit as each one is written.
					</Alert>
				</DialogContent>
				<DialogActions>
					<Button variant="text" onClick={() => dispatch({ type: "CLOSE_MODALS" })}>Cancel</Button>
					<Button variant="contained" onClick={confirmPush}>Confirm push</Button>
				</DialogActions>
			</Dialog>
		);
	};

	// Read-only preview of exactly what pushing would send — reuses the
	// conflict dialog's two-pane comparison layout since it's the same
	// "look at two things side by side before deciding" pattern.
	const renderPreviewDialog = () => {
		const rows = state.previewData ? Object.entries(state.previewData) : [];
		const readyCount = state.units.filter((u) => u.status === "Ready").length;

		// Multiple units frequently share one owner — a management company
		// owning every unit in a building is the common case — and resolveOwner's
		// run cache means the real push only ever creates/updates that owner
		// once. Grouping those units under a single owner pane here (instead of
		// repeating the same JSON per unit with a "(shared with an earlier unit
		// in this batch)" qualifier tacked onto the label) keeps that one real
		// write from reading like several.
		const strippedAction = (action) => (action || "").replace(/ \(shared with an earlier unit in this batch\)$/, "");
		const ownerGroupKey = (owner) => (owner ? `${owner.objectType}:${owner.hubspotId || "new"}:${strippedAction(owner.action)}:${JSON.stringify(owner.properties)}` : "none");

		const groups = [];
		const groupsByKey = new Map();
		rows.forEach(([unitId, p]) => {
			if (p.error) {
				groups.push({ key: `error:${unitId}`, error: true, unitId, p });
				return;
			}
			const key = ownerGroupKey(p.owner);
			let group = groupsByKey.get(key);
			if (!group) {
				group = { key, owner: p.owner, entries: [] };
				groupsByKey.set(key, group);
				groups.push(group);
			}
			group.entries.push({ unitId, p });
		});

		return (
			<Dialog open={state.previewOpen} onClose={() => dispatch({ type: "CLOSE_MODALS" })} maxWidth="md" fullWidth>
				<DialogTitle>
					<Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
						<div>HubSpot push preview</div>
						<IconButton onClick={() => dispatch({ type: "CLOSE_MODALS" })} aria-label="Close">
							<CloseIcon />
						</IconButton>
					</Stack>
					<div style={{ fontSize: 13, color: "rgba(0,0,0,.6)", marginTop: 4, fontWeight: 400 }}>
						Exactly what will be sent for each unit. Nothing is written to HubSpot by viewing this.
					</div>
				</DialogTitle>
				<DialogContent>
					{state.previewLoading && (
						<div style={{ marginTop: 8 }}>
							<LinearProgress />
							<div style={{ marginTop: 10, fontSize: 12, color: "rgba(0,0,0,.6)" }}>
								Checking {readyCount} unit{readyCount === 1 ? "" : "s"} against HubSpot. This looks up each one individually, so larger
								batches can take a little while.
							</div>
						</div>
					)}
					{state.previewError && <Alert severity="error" sx={{ mt: 1 }}>{state.previewError}</Alert>}
					{!state.previewLoading && !state.previewError && groups.map((group) => {
						if (group.error) {
							const unit = state.units.find((u) => String(u.id) === group.unitId);
							return (
								<Alert key={group.key} severity="error" sx={{ mb: 2 }}>
									{unit ? `${unit.a2 || unit.a1}: ` : ""}{group.p.error}
								</Alert>
							);
						}
						const owner = group.owner;
						const multiUnit = group.entries.length > 1;
						const unitLabel = (unitId) => state.units.find((u) => String(u.id) === unitId)?.a2 || `Unit ${unitId}`;
						const headerAddress = state.units.find((u) => String(u.id) === group.entries[0].unitId)?.a1 || "";
						return (
							<div key={group.key} style={{ marginBottom: 20 }}>
								<div style={{ fontWeight: 500, fontSize: 13, marginBottom: 8 }}>
									{group.entries.map(({ unitId }) => unitLabel(unitId)).join(", ")}
									{headerAddress ? ` · ${headerAddress}` : ""}
								</div>
								<div className="unitMiningConflictGrid">
									<div style={{ display: "flex", flexDirection: "column" }}>
										{group.entries.map(({ unitId, p }, idx) => (
											<div
												key={unitId}
												className="unitMiningConflictPane"
												style={{ flex: 1, ...(idx > 0 ? { marginTop: 12 } : null) }}
											>
												<div className="unitMiningConflictPaneHeader">
													<span>Unit ({p.unit.action}){multiUnit ? `, ${unitLabel(unitId)}` : ""}</span>
												</div>
												<div className="unitMiningConflictPaneBody">
													<pre className="unitMiningPreviewJson">{JSON.stringify(p.unit.properties, null, 2)}</pre>
												</div>
											</div>
										))}
									</div>
									<div style={{ display: "flex", flexDirection: "column" }}>
										<div className="unitMiningConflictPane" style={{ flex: 1 }}>
											<div className="unitMiningConflictPaneHeader">
												<span>
													{owner
														? `${owner.objectType === "contacts" ? "Contact" : "Entity"} (${strippedAction(owner.action)}), ${owner.associationLabel}`
														: "No owner to associate"}
												</span>
											</div>
											<div className="unitMiningConflictPaneBody">
												{owner && owner.properties ? (
													<pre className="unitMiningPreviewJson">{JSON.stringify(owner.properties, null, 2)}</pre>
												) : owner ? (
													<span style={{ color: "rgba(0,0,0,.5)" }}>Existing record found. No fields will be written, only associated.</span>
												) : (
													<span style={{ color: "rgba(0,0,0,.5)" }}>—</span>
												)}
											</div>
										</div>
										{group.entries.map(
											({ unitId, p }) =>
												p.owner2 && (
													<div key={`owner2-${unitId}`} className="unitMiningConflictPane" style={{ marginTop: 12, flex: 1 }}>
														<div className="unitMiningConflictPaneHeader">
															<span>
																Co-owner Contact ({p.owner2.action}), {p.owner2.associationLabel}
																{multiUnit ? `, ${unitLabel(unitId)}` : ""}
															</span>
														</div>
														<div className="unitMiningConflictPaneBody">
															{p.owner2.properties ? (
																<pre className="unitMiningPreviewJson">{JSON.stringify(p.owner2.properties, null, 2)}</pre>
															) : (
																<span style={{ color: "rgba(0,0,0,.5)" }}>Existing record found. No fields will be written, only associated.</span>
															)}
														</div>
													</div>
												),
										)}
									</div>
								</div>
							</div>
						);
					})}
				</DialogContent>
				<DialogActions>
					<Button variant="text" onClick={() => dispatch({ type: "CLOSE_MODALS" })}>Close</Button>
				</DialogActions>
			</Dialog>
		);
	};

	// Only "discovery" re-runs need a reconfirm now — Load Selected is
	// non-destructive, so it doesn't need one at all.
	const renderReconfirmDialog = () => {
		const kind = state.reconfirm;
		const body = `This uses more ATTOM lookup credits. Any newly found candidates are added to the ${state.cands.length} already under review. Your existing accept/reject decisions will not be removed.`;
		return (
			<Dialog open={!!kind} onClose={() => dispatch({ type: "CLOSE_MODALS" })} maxWidth="xs" fullWidth>
				<DialogTitle>Run discovery again?</DialogTitle>
				<DialogContent>
					<Alert severity="warning">{body}</Alert>
				</DialogContent>
				<DialogActions>
					<Button variant="text" onClick={() => dispatch({ type: "CLOSE_MODALS" })}>Cancel</Button>
					<Button variant="contained" color="primary" onClick={proceedReconfirm}>Run Discovery Again</Button>
				</DialogActions>
			</Dialog>
		);
	};

	// Shown in place of the stepper + step content until the resume-in-
	// progress-mining fetch settles — a plain "Loading…" would still let the
	// Launch step's layout flash in behind it; this occupies the same space
	// instead so nothing shifts once the real step is known.
	const renderLoadingSkeleton = () => (
		<>
			<div className="unitMiningSkeletonStepper">
				{[0, 1, 2, 3].map((i) => (
					<div key={i} className="unitMiningSkeletonBar unitMiningSkeletonPulse" style={{ width: 90, height: 14 }} />
				))}
			</div>
			<div className="propertyViewerPaperBody">
				<div className="unitMiningSkeletonBar unitMiningSkeletonPulse" style={{ width: "30%", height: 20 }} />
				<div className="unitMiningSkeletonBar unitMiningSkeletonPulse" style={{ width: "60%", height: 14, marginTop: 12 }} />
				<div className="unitMiningSkeletonBar unitMiningSkeletonPulse" style={{ width: "100%", height: 220, marginTop: 20 }} />
			</div>
		</>
	);

	return (
		<section className="unitMiningSection propertyViewerPaper">
			<div className="propertyViewerPaperHeader">
				<div>
					<div className="unitMiningHeaderTitle">
						<TravelExploreIcon fontSize="small" color="primary" />
						<h3>Mine Units</h3>
					</div>
					<p>This property has no units yet. Working data below is local to this run. Nothing is written to HubSpot until the final confirm step.</p>
				</div>
			</div>

			{!state.hydrated ? (
				renderLoadingSkeleton()
			) : (
				<>
					{renderStepper()}

					<div className="propertyViewerPaperBody">
						{showJobOverlay
							? renderJobPanel()
							: state.step === 1
								? renderLaunchStep()
								: state.step === 2
									? renderDiscoveryStep()
									: state.step === 3
										? renderEnrichmentStep()
										: renderPushStep()}
					</div>
				</>
			)}

			{renderJsonDialog()}
			{renderConflictDialog()}
			{renderPdlLookupDialog()}
			{renderPreviewDialog()}
			{renderConfirmDialog()}
			{renderReconfirmDialog()}
		</section>
	);
};

export default UnitMiningWizard;
